import { createProcessingComparator, mergeProcessingGroups } from "../../utils/tianlanProcessingSort";
import { summarizeDeadlines } from "../../utils/tianlanDeadline";
import { createCustomerCountKey } from "../../utils/tianlanCustomerCount";
import TianlanSmsScheduleManager from "./TianlanSmsScheduleManager";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import db from "../../firebaseConfig";
import usePageTitle from "../../hooks/usePageTitle";
import { adminCallable } from "../../utils/adminAuth";
import { formatCalendarDateAnswer } from "../../utils/serviceQuestionnaire";
import { normalizeCombinedFormSettings } from "../../utils/serviceCombinedProducts";
import { getServiceTaskStage } from "../../utils/serviceTaskStatus";
import {
  callJamboProxy,
  getScopedSettingsDocId,
  isInCurrentShop,
  useTianlanStore,
  withShopScope,
} from "../../utils/tianlanStore";

const styleLabelsOf = (item) => {
  const fromOptions = Array.isArray(item?.styleOptions)
    ? item.styleOptions.map(
        (style) =>
          style.label ||
          [style.keyword, style.subKeyword].filter(Boolean).join(" / "),
      )
    : [];
  const fromStyles = Array.isArray(item?.styles) ? item.styles : [];
  return [
    ...new Set(
      [...fromOptions, ...fromStyles]
        .map((style) => String(style || "").trim())
        .filter(Boolean),
    ),
  ];
};

const normalizeStyleKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[／/\\-]/g, "")
    .toLowerCase() || "noStyle";
const normalizeExcelStyle = (value) => {
  const style = String(value || "")
    .trim()
    .replace(/[／/]/g, "-");
  return style === "--" ? "" : style;
};
const chunkArray = (items = [], size = 1) => {
  const chunks = [];
  const safeSize = Math.max(Number(size) || 1, 1);
  for (let index = 0; index < items.length; index += safeSize) {
    chunks.push(items.slice(index, index + safeSize));
  }
  return chunks;
};
const runWithClientConcurrency = async (items = [], limit = 2, iterator) => {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(Number(limit) || 1, 1), list.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < list.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await iterator(list[currentIndex], currentIndex);
      }
    }),
  );
  return results;
};
const EXCEL_SEARCH_BATCH_SIZE = 10;
const EXCEL_SEARCH_CLIENT_CONCURRENCY = 1;
const EXCEL_IMPORT_BATCH_SIZE = 30;
const EXCEL_IMPORT_CLIENT_CONCURRENCY = 1;
const commodityIdOf = (item) =>
  String(item?.commodityId || item?.id || item?.jamboCommodityId || "");
const stockSettingOf = (product, style = "") => {
  const styleKey = normalizeStyleKey(style);
  const setting =
    product?.stockSettings?.[styleKey] ||
    product?.manualStockByStyle?.[styleKey] ||
    product?.stockSettings?.noStyle ||
    product?.manualStockByStyle?.noStyle ||
    {};
  const mode = setting.mode || setting.manualStockMode || "limited";
  const quantity =
    Number(setting.quantity ?? setting.manualStockQuantity ?? 0) || 0;
  const reservedQuantity = Number(setting.reservedQuantity || 0) || 0;
  return {
    mode: mode === "unlimited" ? "unlimited" : "limited",
    quantity,
    reservedQuantity,
    remainingQuantity:
      mode === "unlimited" ? null : Math.max(quantity - reservedQuantity, 0),
  };
};
const stockTextOf = (product, style = "") => {
  const stock = stockSettingOf(product, style);
  if (stock.mode === "unlimited") return "可選無上限";
  return `可選 ${stock.quantity} / 已保留 ${stock.reservedQuantity} / 剩餘 ${stock.remainingQuantity}`;
};
const productHasSelectableStock = (product = {}) => {
  const styles = Array.isArray(product.styles) && product.styles.length > 0
    ? product.styles
    : [""];
  return styles.some((style) => {
    const stock = stockSettingOf(product, style);
    return stock.mode === "unlimited" || stock.remainingQuantity > 0;
  });
};
const hasNoSelectableExchangeOptions = (item = {}) => {
  if (isExchangeCompleted(item)) return false;
  const options = Array.isArray(item.availableExchanges)
    ? item.availableExchanges
    : [];
  if (item.noExchangeOptions === true) return true;
  if (Array.isArray(item.availableExchanges) && options.length === 0) return true;
  return options.length > 0 && !options.some(productHasSelectableStock);
};
const styleMatchesRuleStyles = (ruleStyles = [], sourceStyle = "") => {
  if (!Array.isArray(ruleStyles) || ruleStyles.length === 0) return true;
  const sourceKey = normalizeStyleKey(sourceStyle);
  if (!sourceKey || sourceKey === "noStyle") return true;
  return ruleStyles.some((style) => {
    const styleKey = normalizeStyleKey(
      typeof style === "string"
        ? style
        : style?.label || style?.keyword || style?.name || "",
    );
    return styleKey && styleKey === sourceKey;
  });
};
const sourceStyleCandidatesForItem = (rules = [], item = {}) => {
  const sourceId = String(
    item.jamboCommodityId ||
      item.originalProduct?.jamboCommodityId ||
      item.originalProduct?.commodityId ||
      item.originalProduct?.id ||
      "",
  );
  if (!sourceId) return [];
  const styles = [];
  rules
    .filter((rule) => rule.enabled !== false)
    .forEach((rule) => {
      const sourceIds = (rule.sourceCommodityIds || [rule.sourceCommodityId])
        .map(String)
        .filter(Boolean);
      if (!sourceIds.includes(sourceId)) return;
      const sourceProduct = (rule.sourceProducts || []).find(
        (product) => commodityIdOf(product) === sourceId,
      );
      const selectedStyles = Array.isArray(sourceProduct?.selectedStyles)
        ? sourceProduct.selectedStyles
        : [];
      selectedStyles.forEach((style) => {
        const text = String(
          typeof style === "string"
            ? style
            : style?.label || style?.keyword || style?.name || "",
        ).trim();
        if (text) styles.push(text);
      });
    });
  return [...new Set(styles)];
};
const hasRuleStyleMismatchExchangeItem = (item, rules = []) => {
  if (isExchangeCompleted(item)) return false;
  const candidates = sourceStyleCandidatesForItem(rules, item);
  if (candidates.length === 0) return false;
  const originalStyle = item.originalStyle || item.style || "";
  if (!originalStyle) return true;
  return !candidates.some(
    (style) => normalizeStyleKey(style) === normalizeStyleKey(originalStyle),
  );
};
const isStyleMismatchExchangeItem = (item, rules = []) =>
  hasRuleStyleMismatchExchangeItem(item, rules) ||
  hasNoSelectableExchangeOptions(item);
const isExchangeCompleted = (item = {}) =>
  item.status === "exchanged" ||
  item.jamboExchangeStatus === "success" ||
  item.jobStatus === "success";
const ruleMatchesExchangeItem = (rule, item) => {
  const sourceId = String(
    item.jamboCommodityId ||
      item.originalProduct?.jamboCommodityId ||
      item.originalProduct?.commodityId ||
      item.originalProduct?.id ||
      "",
  );
  if (
    !sourceId ||
    !(rule.sourceCommodityIds || []).map(String).includes(sourceId)
  )
    return false;
  const originalStyleKey = normalizeStyleKey(
    item.originalStyle || item.style || "",
  );
  const sourceProduct = (rule.sourceProducts || []).find(
    (product) => commodityIdOf(product) === sourceId,
  );
  const selectedStyles = Array.isArray(sourceProduct?.selectedStyles)
    ? sourceProduct.selectedStyles
    : [];
  return styleMatchesRuleStyles(selectedStyles, originalStyleKey);
};
const liveOptionsForItem = (item, rules) =>
  rules
    .filter(
      (rule) => rule.enabled !== false && ruleMatchesExchangeItem(rule, item),
    )
    .flatMap((rule) => rule.targetProducts || []);
const liveSelectedStockForItem = (item, liveOptions) => {
  const selection = item.customerSelection || {};
  if (selection.action !== "exchange") return null;
  const targetId = String(
    selection.productId ||
      selection.commodityId ||
      selection.jamboCommodityId ||
      "",
  );
  const target = liveOptions.find(
    (product) => commodityIdOf(product) === targetId,
  );
  if (!target) return null;
  return {
    product: target,
    text: stockTextOf(target, selection.style || ""),
  };
};

const selectedTargetProductOf = (item) => {
  const selection = item.customerSelection || {};
  const targetId = String(
    selection.productId ||
      selection.commodityId ||
      selection.jamboCommodityId ||
      "",
  );
  if (!targetId) return null;
  return [
    ...(item.availableExchanges || []),
    ...(item.liveExchangeOptions || []),
  ].find((product) => commodityIdOf(product) === targetId);
};

const selectionQuantityOf = (item) =>
  Math.max(
    Number(item.stockReservation?.quantity || 0) || 0,
    Number(item.takeQty || 0) || 0,
    Number(item.quantity || 0) || 0,
    1,
  );

const EXCHANGE_DATA_STATUS_LABELS = {
  unfilled: "待客人選擇",
  filled: "客人已選，待換貨",
  refund_requested: "不換貨申請退款",
  exchanged: "已換貨",
  handled: "已處理",
  failed: "換貨失敗，請處理",
  out_of_stock: "賣家斷貨",
  stockout_refunded: "已執行斷貨退紅利",
  cancelled: "已取消",
};

const EXCHANGE_JOB_STATUS_LABELS = {
  awaiting_reward_input: "等待輸入差額",
  pending: "等待換貨",
  running: "換貨中",
  success: "換貨成功",
  failed: "換貨失敗",
  restore_failed: "價格還原失敗",
  cancelled: "已取消",
  prepared: "已產生",
  sent: "已發送",
};

const exchangeDataStatusLabelOf = (status) =>
  EXCHANGE_DATA_STATUS_LABELS[status || "unfilled"] || "待客人選擇";

const exchangeJobStatusLabelOf = (status) =>
  EXCHANGE_JOB_STATUS_LABELS[status || "pending"] || "等待換貨";

const smsStatusLabelOf = (status) =>
  ({
    prepared: "已產生預覽",
    queued: "等待發送",
    sending: "發送中",
    sent: "已發送",
    failed: "發送失敗",
  })[status] || "未發送";

const differenceRefundLabelOf = (item = {}) => {
  const status = item.exchangeDifferenceRefundStatus;
  const amount = Math.max(0, Number(item.exchangeDifferenceRefundAmount || 0) || 0);
  if (status === "success") return `已退 ${amount} 點`;
  if (status === "not_required") return item.exchangeDifferenceRefundReason === "disabled" ? "未啟用" : "已確認 0 點";
  if (status === "sending") return "退還中";
  if (status === "failed") return "退還失敗";
  if (status === "blocked") return "請輸入差額";
  if (status === "uncertain") return "結果不確定，請核對";
  if (item.customerSelection?.refundPriceDifference === true) {
    if (item.exchangeDifferenceRefundAmountSet === true) {
      return `已確認 ${Math.max(0, Number(item.exchangeDifferenceRefundAmountInput || 0) || 0)} 點`;
    }
    return "請輸入差額";
  }
  return "未啟用";
};

const completionSmsLabelOf = (item = {}) => {
  const status = item.exchangeCompletionSmsStatus;
  if (status === "queued") return "等待發送";
  if (status === "sending") return "發送中";
  if (status === "sent") return "已發送";
  if (status === "failed") return "發送失敗";
  return isExchangeCompleted(item) ? "待處理" : "換貨成功後自動發送";
};

const serviceTaskLabelOf = (task = {}) => {
  const stage = getServiceTaskStage(task);
  if (stage === "handled") return "已處理";
  if (stage === "submitted") return "客人已送出";
  if (stage === "expired") return "已過期";
  return "待客人填寫";
};

const serviceTaskToneOf = (label) => {
  if (label === "已處理") return "success";
  if (label === "已過期") return "danger";
  if (label === "客人已送出") return "warning";
  return "neutral";
};

const statusLabelOf = (item, itemJobs = []) => {
  if (item.sellerOutOfStockExecutedAt || item.sellerOutOfStockRefundStatus === "success")
    return "已執行斷貨退紅利";
  if (item.status === "handled")
    return "已處理";
  if (item.status === "out_of_stock" || item.customerSelection?.cancelReason === "seller_out_of_stock")
    return "賣家斷貨";
  if (
    item.jamboExchangeStatus === "restore_failed" ||
    item.jobStatus === "restore_failed" ||
    itemJobs.some((job) => job.status === "restore_failed")
  )
    return "價格還原失敗，請檢查";
  if (
    item.status === "failed" ||
    item.jamboExchangeStatus === "failed" ||
    item.jobStatus === "failed" ||
    itemJobs.some((job) => ["failed", "restore_failed"].includes(job.status))
  )
    return "換貨失敗，請處理";
  if (
    item.status === "exchanged" ||
    item.jamboExchangeStatus === "success" ||
    item.jobStatus === "success" ||
    itemJobs.some((job) => job.status === "success")
  )
    return "已換貨";
  if (!item.customerSelection?.action && isDeadlineExpired(item))
    return "已過期";
  if (
    item.status === "refund_requested" ||
    item.customerSelection?.action === "cancel"
  )
    return "不換貨申請退款";
  if (item.customerSelection?.action) return "客人已選，待換貨";
  return "待客人選擇";
};

const statusToneOf = (label) => {
  if (label.includes("失敗") || label.includes("還原")) return "danger";
  if (label.includes("已換貨") || label.includes("已處理") || label.includes("已執行")) return "done";
  if (label.includes("過期")) return "danger";
  if (label.includes("賣家斷貨")) return "warning";
  if (label.includes("退款")) return "warning";
  if (label.includes("客人已選")) return "ready";
  return "pending";
};

const searchableTextOf = (item) =>
  [
    item.paNumber,
    item.recipientPhone,
    item.fromName,
    item.originalProduct?.name,
    item.originalProduct?.itemNumber,
    item.customerSelection?.productName,
    item.customerSelection?.itemNumber,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const mergeUnique = (items) => [
  ...new Set(items.map((item) => String(item || "").trim()).filter(Boolean)),
];
const orderItemIdOf = (order) =>
  String(order?.jamboOrderItemId || order?.orderId || order?.id || "");
const selectedTakeQtyOf = (order, selectedOrders) =>
  Math.max(
    Number(
      selectedOrders.find((item) => orderItemIdOf(item) === orderItemIdOf(order))
        ?.takeQty,
    ) || 0,
    0,
  );

const availableImportQtyOf = (order) =>
  Math.max(
    Number(order?.availableImportQty ?? order?.quantity ?? 0) || 0,
    0,
  );

const isOrderSelectable = (order) => availableImportQtyOf(order) > 0;

const orderSortDisplayTextOf = (order) => {
  if (!order) return "";
  if (order.sortTimeText) return order.sortTimeText;
  if (order.sortMode === "latestClosed") {
    return (
      order.billCreatedAtText ||
      order.createdAtText ||
      order.billPaidAtText ||
      order.paidAtText ||
      (order.sortTimeMs ? formatDeadline(order.sortTimeMs) : "")
    );
  }
  return (
    order.billPaidAtText ||
    order.paidAtText ||
    order.billCreatedAtText ||
    order.createdAtText ||
    (order.sortTimeMs ? formatDeadline(order.sortTimeMs) : "")
  );
};

const isDeadlineExpired = (item) => {
  if (!item?.exchangeDeadline) return false;
  const deadlineMs = new Date(item.exchangeDeadline).getTime();
  return Number.isFinite(deadlineMs) && deadlineMs < Date.now();
};

const timeMsOf = (value) => {
  if (!value) return 0;
  if (value?.toDate) {
    const date = value.toDate();
    return Number.isFinite(date.getTime()) ? date.getTime() : 0;
  }
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    return Number(value.seconds) * 1000;
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const selectionTimeValueOf = (item) =>
  item.selectedAt ||
  item.submittedAt ||
  item.customerSelectedAt ||
  item.customerSelection?.selectedAt ||
  item.customerSelection?.submittedAt ||
  item.customerSelection?.createdAt ||
  item.customerSelection?.updatedAt ||
  (item.customerSelection?.action ? item.updatedAt : "");

const selectionTimeMsOf = (item) => timeMsOf(selectionTimeValueOf(item));

const createdTimeMsOf = (item) =>
  timeMsOf(item.createdAt || item.createdTime || item.importedAt || item.updatedAt);

const deadlineTimeMsOf = (item) => timeMsOf(item.exchangeDeadline);

const paSortValueOf = (item) => {
  const value = String(item.paNumber || item.jamboOrderId || item.orderId || "");
  const digits = value.match(/\d+/g)?.join("") || "";
  return Number(digits) || 0;
};

const formatDeadline = (value) => {
  if (!value) return "";
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatExcelDateTime = (value) => {
  const text = formatDeadline(value);
  return text || "";
};

const formatPaNumber = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.toUpperCase().startsWith("PA") ? text : `PA${text}`;
};

const serviceAnswerIsFilled = (value) => {
  if (Array.isArray(value)) return value.some(serviceAnswerIsFilled);
  if (value && typeof value === "object") {
    if (value.toDate) return true;
    return Object.values(value).some(serviceAnswerIsFilled);
  }
  return String(value ?? "").trim() !== "";
};

const formatServiceAnswerForDisplay = (value, question = {}) => {
  if (!serviceAnswerIsFilled(value)) return "";
  if (question.type === "calendar_date" || (value && typeof value === "object" && value.calendar)) {
    return formatCalendarDateAnswer(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => formatServiceAnswerForDisplay(item, question))
      .filter(Boolean)
      .join("、");
  }
  if (value && typeof value === "object") {
    if (value.toDate) return formatDeadline(value);
    return Object.entries(value)
      .map(([key, item]) => {
        const text = formatServiceAnswerForDisplay(item);
        return text ? `${key}：${text}` : "";
      })
      .filter(Boolean)
      .join("、");
  }
  return String(value ?? "").trim();
};

const serviceQuestionRowsOf = (task = {}, template = {}) => {
  const responses = task.responses || {};
  const responseLabels = task.responseLabels || {};
  const questions = Array.isArray(template.questions) ? template.questions : [];
  const seenIds = new Set();
  const rows = questions.map((question, index) => {
    const id = String(question.id || `question_${index + 1}`).trim();
    seenIds.add(id);
    const value = responses[id];
    return {
      id,
      label:
        String(question.label || responseLabels[id] || "").trim() ||
        `填寫項目 ${index + 1}`,
      answerText: formatServiceAnswerForDisplay(value, question),
      filled: serviceAnswerIsFilled(value),
    };
  });

  Object.entries(responses).forEach(([id, value]) => {
    if (seenIds.has(id)) return;
    rows.push({
      id,
      label: String(responseLabels[id] || id || "其他回覆").trim(),
      answerText: formatServiceAnswerForDisplay(value),
      filled: serviceAnswerIsFilled(value),
    });
  });

  return rows;
};

function TianlanJamboOrderManager() {
  usePageTitle("訂單售後服務後台", "天藍小舖後台", "建立與處理訂單售後任務");
  const currentShop = useTianlanStore();
  const [selectedCommodity, setSelectedCommodity] = useState(null);
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [orders, setOrders] = useState([]);
  const [exchangeItems, setExchangeItems] = useState([]);
  const [serviceTasks, setServiceTasks] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [exchangeRules, setExchangeRules] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [taskType, setTaskType] = useState("exchange");
  const [selectedServiceTaskIds, setSelectedServiceTaskIds] = useState([]);
  const [importMode, setImportMode] = useState("all");
  const [missingQty, setMissingQty] = useState(1);
  const [refundDifferenceEnabled, setRefundDifferenceEnabled] = useState(false);
  const [refundDifferenceAmount, setRefundDifferenceAmount] = useState("");
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [excelPreview, setExcelPreview] = useState(null);
  const [excelPreviewProgress, setExcelPreviewProgress] = useState(null);
  const [selectedExcelRowKeys, setSelectedExcelRowKeys] = useState(new Set());
  const [searchSummary, setSearchSummary] = useState(null);
  const [searchBegin, setSearchBegin] = useState("");
  const [searchEnd, setSearchEnd] = useState("");
  const [sortMode, setSortMode] = useState("latestPaid");
  const [onlyUnpreparedOrders, setOnlyUnpreparedOrders] = useState(true);
  const [ruleKeyword, setRuleKeyword] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [processingSortMode, setProcessingSortMode] = useState("paDesc");
  const [queueKeyword, setQueueKeyword] = useState("");
  const [workspaceView, setWorkspaceView] = useState("queue");
  const [customerLinkDialog, setCustomerLinkDialog] = useState({
    open: false,
    paNumber: "",
    expiresInHours: 168,
    url: "",
    qrDataUrl: "",
    expiresAt: "",
    loading: false,
    copied: false,
    error: "",
  });
  const [selectedExchangeIds, setSelectedExchangeIds] = useState([]);
  const [differenceRewardInputs, setDifferenceRewardInputs] = useState({});
  const [smsAccessLinkDays, setSmsAccessLinkDays] = useState(7);
  const [smsDialog, setSmsDialog] = useState({
    open: false,
    mode: "immediate",
    messages: [],
    copied: false,
    preparing: false,
    sending: false,
    excludeSent: true,
    sendFbMessenger: true,
    smsKind: "exchange",
    quickConfirm: null,
    sentResults: [],
  });
  const [deadlineDialog, setDeadlineDialog] = useState({
    open: false,
    deadline: "",
    groups: [],
    hasDeadline: false,
    target: "exchange",
  });
  const [removeDialog, setRemoveDialog] = useState({
    open: false,
    ids: [],
    target: "exchange",
    title: "",
    message: "",
  });
  const [returnDialog, setReturnDialog] = useState({
    open: false,
    ids: [],
    title: "",
    message: "",
    mode: "return",
  });
  const [stockoutRefundDialog, setStockoutRefundDialog] = useState({
    open: false,
    ids: [],
  });
  const [differenceTaskDialog, setDifferenceTaskDialog] = useState({
    open: false,
    itemId: "",
    paNumber: "",
    enabled: false,
    amount: "",
  });
  const [autoRunSettings, setAutoRunSettings] = useState({
    jamboAutoRun: false,
    loaded: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadData = async () => {
    const [exchangeSnap, serviceSnap, templateSnap, legacyFormSnap, jobsSnap, rulesSnap, settingsSnap] = await Promise.all(
      [
        getDocs(
          query(
            collection(db, "tianlan_exchange_data"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDocs(
          query(
            collection(db, "tianlan_service_tasks"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDocs(
          query(
            collection(db, "tianlan_service_question_templates"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDocs(
          query(
            collection(db, "tianlan_after_sales_forms"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDocs(
          query(
            collection(db, "tianlan_jambo_exchange_jobs"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDocs(
          query(
            collection(db, "tianlan_exchange_rules"),
            where("shopUserId", "==", currentShop.shopUserId),
          ),
        ),
        getDoc(
          doc(
            db,
            "tianlan_settings",
            getScopedSettingsDocId("messages", currentShop),
          ),
        ),
      ],
    );
    setSmsAccessLinkDays(Number(settingsSnap.data()?.smsAccessLinkDays) || 7);
    setAutoRunSettings({
      jamboAutoRun: settingsSnap.exists()
        ? settingsSnap.data().jamboAutoRun === true
        : false,
      loaded: true,
    });
    const rules = rulesSnap.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((item) => isInCurrentShop(item, currentShop));
    setExchangeRules(rules);
    const templatesById = new Map();
    [...legacyFormSnap.docs, ...templateSnap.docs].forEach((item) => {
      const data = { id: item.id, ...item.data() };
      if (isInCurrentShop(data, currentShop)) templatesById.set(item.id, data);
    });
    const templates = [...templatesById.values()].sort((a, b) =>
      String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")),
    );
    setServiceTemplates(templates);
    setServiceTasks(
      serviceSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => isInCurrentShop(item, currentShop))
        .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || ""))),
    );
    setExchangeItems(
      exchangeSnap.docs
        .map((item) => {
          const exchangeItem = { id: item.id, ...item.data() };
          const liveExchangeOptions = liveOptionsForItem(exchangeItem, rules);
          return {
            ...exchangeItem,
            liveExchangeOptions,
            liveSelectedStock: liveSelectedStockForItem(
              exchangeItem,
              liveExchangeOptions,
            ),
          };
        })
        .filter((item) => isInCurrentShop(item, currentShop)),
    );
    setJobs(
      jobsSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => isInCurrentShop(item, currentShop)),
    );
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShop.shopUserId]);

  useEffect(() => {
    if (importMode !== "all" && selectedCommodities.length > 1) {
      const first = selectedCommodities[0];
      setSelectedCommodities(first ? [first] : []);
      setSelectedCommodity(first || null);
      setTaskType(taskTypeOfCommodity(first || {}));
      setOrders([]);
      setSelectedOrders([]);
      setSearchSummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importMode]);

  const taskTypeOfCommodity = (commodity = {}) =>
    commodity?.ruleTaskType === "form" ||
    (Number(commodity?.targetCount || 0) <= 0 &&
      (commodity?.formTaskTemplateId || commodity?.questionTemplateId))
      ? "form"
      : "exchange";

  const taskDifferenceSettingPayload = () => {
    if (taskType !== "exchange" || !refundDifferenceEnabled) {
      return { refundPriceDifference: false, differenceRewardAmountSet: false };
    }
    const raw = String(refundDifferenceAmount).trim();
    const amount = Number(raw);
    if (raw === "" || !Number.isInteger(amount) || amount < 0 || amount > 1000000) {
      throw new Error("差額紅利請輸入 0 到 1,000,000 的整數點數");
    }
    return {
      refundPriceDifference: true,
      differenceRewardAmount: amount,
      differenceRewardAmountSet: true,
    };
  };

  const createServiceTasksFromOrders = async (ordersToCreate) => {
    if (taskType !== "form") return { createdCount: 0 };
    const effectiveTemplateId =
      selectedCommodity?.formTaskTemplateId ||
      selectedCommodity?.questionTemplateId;
    const hasOrderTemplates = ordersToCreate.every((order) => (
      order.questionTemplateId ||
      order.formTaskTemplateId ||
      order.selectedCommodity?.questionTemplateId ||
      order.selectedCommodity?.formTaskTemplateId ||
      effectiveTemplateId
    ));
    if (!hasOrderTemplates) {
      throw new Error("這個商品規則尚未設定售後表單，請先到「商品規則管理」設定題目。");
    }
    const createTasks = adminCallable("tianlanCreateServiceTasksFromOrders");
    const response = await createTasks(
      withShopScope(
        {
          taskType: "form",
          questionTemplateId: effectiveTemplateId,
          formResponsePolicy: selectedCommodity?.formResponsePolicy,
          orders: ordersToCreate,
          product: selectedCommodity || {},
        },
        currentShop,
      ),
    );
    return response.data || {};
  };

  const applyLocalServiceTaskImportCounts = (ordersToAdjust = [], commodity = selectedCommodity) => {
    if (taskType !== "form" || !commodity) return ordersToAdjust;
    const sourceId = String(
      commodity.commodityId ||
        commodity.jamboCommodityId ||
        commodity.id ||
        "",
    ).trim();
    const sourceItemNumber = String(
      commodity.partNumber ||
        commodity.itemNumber ||
        commodity.sku ||
        "",
    )
      .trim()
      .toUpperCase();
    const exactCounts = new Map();
    const itemCounts = new Map();
    const paCounts = new Map();
    const seenServiceGroups = new Set();
    const addCount = (map, key, count) => {
      if (!key) return;
      map.set(key, (map.get(key) || 0) + count);
    };
    serviceTasks.forEach((task) => {
      if (task.taskType === "exchange") return;
      const serviceGroupKey = String(task.serviceGroupKey || "").trim();
      const combinedImportKey = task.formProductMode === "children"
        ? [
          "combined",
          task.paNumber || task.billId || "",
          task.combinedParentProduct?.commodityId || task.combinedParentProduct?.id || "",
          task.questionTemplateId || task.formId || "",
          ...(Array.isArray(task.sourceOrderItemIds) ? [...task.sourceOrderItemIds].sort() : []),
        ].join("@@")
        : serviceGroupKey;
      if (combinedImportKey && seenServiceGroups.has(combinedImportKey)) return;
      if (combinedImportKey) seenServiceGroups.add(combinedImportKey);
      const taskSourceId = String(
        task.combinedParentProduct?.jamboCommodityId ||
          task.combinedParentProduct?.commodityId ||
          task.combinedParentProduct?.id ||
          task.jamboCommodityId ||
          task.originalProduct?.jamboCommodityId ||
          task.originalProduct?.commodityId ||
          task.originalProduct?.id ||
          task.product?.jamboCommodityId ||
          task.product?.commodityId ||
          task.product?.id ||
          "",
      ).trim();
      const taskItemNumber = String(
        task.combinedParentProduct?.itemNumber ||
          task.combinedParentProduct?.partNumber ||
          task.originalProduct?.itemNumber ||
          task.originalProduct?.partNumber ||
          task.product?.itemNumber ||
          task.product?.partNumber ||
          task.itemNumber ||
          task.partNumber ||
          "",
      )
        .trim()
        .toUpperCase();
      if (!sourceId || taskSourceId !== sourceId) return;
      const taskStyleKey = normalizeStyleKey(
        task.formProductMode === "children" ? "" : (task.originalStyle || task.style || ""),
      );
      const taskOrderId = orderItemIdOf(task);
      const taskPa = String(task.paNumber || task.billId || task.jamboOrderId || "")
        .trim()
        .toUpperCase();
      const count = serviceGroupKey
        ? Math.max(
          Number(task.sourceBundleQuantity || 0) || 0,
          Number(task.sourceQuantity || task.originalQuantity || 0) || 0,
          1,
        )
        : Math.max(
          Number(task.matchedTakeQty || 0) || 0,
          Number(task.takeQty || 0) || 0,
          Number(task.quantity || 0) || 0,
          1,
        );
      if (!serviceGroupKey) {
        if (taskOrderId && taskSourceId) {
          addCount(exactCounts, `${taskOrderId}@@${taskSourceId}@@${taskStyleKey}`, count);
        }
        if (taskOrderId && taskItemNumber) {
          addCount(itemCounts, `${taskOrderId}@@${taskItemNumber}@@${taskStyleKey}`, count);
        }
      }
      if (taskPa && taskItemNumber) {
        addCount(paCounts, `${taskPa}@@${taskItemNumber}@@${taskStyleKey}`, count);
      }
    });
    return ordersToAdjust.map((order) => {
      const orderId = orderItemIdOf(order);
      const orderStyleKey = normalizeStyleKey(order.style || order.originalStyle || "");
      const orderSourceId = String(order.commodityId || sourceId || "").trim();
      const orderItemNumber = String(order.itemNumber || sourceItemNumber || "")
        .trim()
        .toUpperCase();
      const orderPa = String(order.paNumber || order.billId || order.jamboOrderId || "")
        .trim()
        .toUpperCase();
      const exactCount = Math.max(
        Number(order.existingExchangeCount || 0) || 0,
        exactCounts.get(`${orderId}@@${orderSourceId}@@${orderStyleKey}`) || 0,
        itemCounts.get(`${orderId}@@${orderItemNumber}@@${orderStyleKey}`) || 0,
      );
      const paKey = `${orderPa}@@${orderItemNumber}@@${orderStyleKey}`;
      let paRemaining = paCounts.get(paKey) || 0;
      if (exactCount > 0) paRemaining = Math.max(paRemaining - exactCount, 0);
      const quantity = Math.max(Number(order.quantity || 0) || 0, 0);
      const fallbackCount = Math.min(paRemaining, Math.max(quantity - exactCount, 0));
      paCounts.set(paKey, Math.max(paRemaining - fallbackCount, 0));
      const existingExchangeCount = Math.min(quantity, exactCount + fallbackCount);
      const availableImportQty = Math.max(quantity - existingExchangeCount, 0);
      return {
        ...order,
        existingTaskCount: existingExchangeCount,
        existingExchangeCount,
        availableImportQty,
        isDuplicateExchangeTask: availableImportQty <= 0,
        duplicateReason:
          availableImportQty <= 0
            ? order.duplicateReason || "相同訂單的相同表單任務已存在"
            : "",
      };
    });
  };

  const searchOrders = async () => {
    if (!selectedCommodity?.commodityId) {
      alert("請先從「商品規則」選擇任務商品");
      return;
    }
    setIsLoading(true);
    try {
      setExcelPreview(null);
      setSelectedExcelRowKeys(new Set());
      const commoditiesToSearch =
        importMode === "all" && selectedCommodities.length > 0
          ? selectedCommodities
          : [selectedCommodity].filter(Boolean);
      const results = [];
      for (const commodity of commoditiesToSearch) {
        const isMultiSearch = commoditiesToSearch.length > 1;
        const commodityTaskType = taskTypeOfCommodity(commodity);
        const result = await callJamboProxy(
          "searchOrdersByCommodity",
          {
            commodityId: commodity.commodityId,
            productName: commodity.name || "",
            itemNumber: commodity.partNumber || "",
            searchBegin,
            searchEnd,
            styleKeywords: isMultiSearch ? (commodity.selectedStyles || []) : selectedStyles,
            mode: importMode,
            missingQty,
            sortMode,
            onlyUnprepared: onlyUnpreparedOrders,
            taskType: commodityTaskType,
            questionTemplateId:
              commodityTaskType === "form"
                ? commodity?.formTaskTemplateId ||
                  commodity?.questionTemplateId ||
                  ""
                : "",
          },
          currentShop,
        );
        const attachCommodity = (order) => ({
          ...order,
          selectedCommodity: commodity,
          selectedCommodityId: commodity.commodityId || commodity.id || "",
          questionTemplateId: commodity.questionTemplateId || commodity.formTaskTemplateId || "",
          formTaskTemplateId: commodity.formTaskTemplateId || commodity.questionTemplateId || "",
          formResponsePolicy: commodity.formResponsePolicy || { mode: "per_unit", quantityPerResponse: 1 },
          ruleTaskType: commodityTaskType,
        });
        const adjustedOrders = applyLocalServiceTaskImportCounts(
          (result.orders || []).map(attachCommodity),
          commodity,
        );
        const adjustedById = new Map(adjustedOrders.map((order) => [orderItemIdOf(order), order]));
        const adjustedSelectedOrders = (result.selectedOrders || [])
          .map((order) => {
            const adjustedOrder = adjustedById.get(orderItemIdOf(order)) || attachCommodity(order);
            const requestedTakeQty = Number(order.takeQty || 0) || availableImportQtyOf(adjustedOrder);
            const takeQty = Math.min(availableImportQtyOf(adjustedOrder), requestedTakeQty);
            return { ...adjustedOrder, takeQty };
          })
          .filter((order) => availableImportQtyOf(order) > 0 && Number(order.takeQty || 0) > 0);
        results.push({ result, adjustedOrders, adjustedSelectedOrders });
      }
      const adjustedOrders = results
        .flatMap((item) => item.adjustedOrders)
        .sort((a, b) => (Number(b.sortTimeMs || 0) || 0) - (Number(a.sortTimeMs || 0) || 0));
      const adjustedSelectedOrders = results
        .flatMap((item) => item.adjustedSelectedOrders)
        .sort((a, b) => (Number(b.sortTimeMs || 0) || 0) - (Number(a.sortTimeMs || 0) || 0));
      setOrders(adjustedOrders);
      setSelectedOrders(adjustedSelectedOrders);
      setSearchSummary({
        rawCount: results.reduce((sum, item) => sum + (Number(item.result.rawCount || 0) || 0), 0),
        filteredCount: results.reduce((sum, item) => sum + (Number(item.result.filteredCount || item.adjustedOrders.length || 0) || 0), 0),
        selectedQty: adjustedSelectedOrders.reduce(
          (sum, order) => sum + (Number(order.takeQty || 0) || 0),
          0,
        ),
        onlyUnprepared: results.some((item) => item.result.onlyUnprepared === true),
        sortMode: results[0]?.result.sortMode || sortMode,
        zeroQuantitySkippedCount: results.reduce((sum, item) => sum + (Number(item.result.zeroQuantitySkippedCount || 0) || 0), 0),
        paCount: new Set(adjustedOrders.map((order) => order.paNumber || order.billId || order.jamboOrderId).filter(Boolean)).size,
        itemQuantityTotal: adjustedOrders.reduce((sum, order) => sum + (Number(order.quantity || 0) || 0), 0),
        paSummaries: results.flatMap((item) => item.result.paSummaries || []),
      });
    } catch (error) {
      alert(`查詢直播訂單失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const downloadOutOfStockExcelTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      { 料號: "TS826", 樣式: "S-黑", 數量: 2 },
      { 料號: "A001", 樣式: "", 數量: 1 },
    ]);
    worksheet["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 10 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "缺貨搜尋");
    XLSX.writeFile(workbook, "直播缺貨訂單搜尋範本.xlsx");
  };

  const handleOutOfStockExcelPreview = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    setExcelPreview(null);
    setSelectedExcelRowKeys(new Set());
    setExcelPreviewProgress({
      active: true,
      stage: "正在讀取 Excel",
      completedRows: 0,
      searchableRows: 0,
      totalRows: 0,
      completedBatches: 0,
      totalBatches: 0,
      canImportRows: 0,
      failedRows: 0,
    });
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const merged = new Map();
      const invalidRows = [];
      rows.forEach((row, index) => {
        const sourceRow = index + 2;
        const itemNumber = String(
          row["料號"] ||
            row["商品料號"] ||
            row.itemNumber ||
            row.partNumber ||
            "",
        ).trim();
        const style = normalizeExcelStyle(row["樣式"] || row["款式"] || row.style || "");
        const rawQuantity = row["數量"] || row.quantity || row.qty || "";
        const quantity = Number(rawQuantity);
        const errors = [];
        if (!itemNumber) errors.push("缺少料號");
        if (!Number.isFinite(quantity) || quantity <= 0)
          errors.push("數量需大於 0");
        if (errors.length > 0) {
          invalidRows.push({
            rowKey: `invalid__${sourceRow}`,
            itemNumber: itemNumber || "未填料號",
            style,
            quantity: Number.isFinite(quantity) ? quantity : 0,
            sourceRows: [sourceRow],
            canImport: false,
            errors,
            orders: [],
            selectedOrders: [],
            selectedQty: 0,
            paCount: 0,
            itemQuantityTotal: 0,
          });
          return;
        }
        const key = `${itemNumber}__${style}`;
        const current = merged.get(key) || {
          rowKey: key,
          itemNumber,
          style,
          quantity: 0,
          sourceRows: [],
        };
        current.quantity += quantity;
        current.sourceRows.push(sourceRow);
        merged.set(key, current);
      });
      const previewRows = [...merged.values()];
      if (previewRows.length === 0 && invalidRows.length === 0) {
        setExcelPreviewProgress(null);
        alert("Excel 沒有資料，請確認欄位包含：料號、樣式、數量");
        return;
      }
      const updatePreviewState = (nextRows, progressPatch = {}) => {
        const importableKeys = nextRows
          .filter((row) => row.canImport)
          .map((row) => row.rowKey);
        setExcelPreview({
          success: true,
          rows: nextRows,
          invalidCount: invalidRows.length,
          totalRows: rows.length,
          selectedOrders: nextRows.flatMap((row) =>
            row.canImport ? row.selectedOrders || [] : [],
          ),
          selectedQty: nextRows.reduce(
            (sum, row) =>
              sum + (row.canImport ? Number(row.selectedQty || 0) : 0),
            0,
          ),
          paCount: new Set(
            nextRows.flatMap((row) =>
              (row.selectedOrders || [])
                .map((order) => order.paNumber)
                .filter(Boolean),
            ),
          ).size,
          sortMode,
        });
        setSelectedExcelRowKeys(new Set(importableKeys));
        setExcelPreviewProgress((prev) => ({
          ...(prev || {}),
          ...progressPatch,
        }));
      };
      updatePreviewState(invalidRows, {
        active: true,
        stage: previewRows.length > 0 ? "準備批次查詢直播訂單" : "已完成",
        completedRows: 0,
        searchableRows: previewRows.length,
        totalRows: rows.length,
        completedBatches: 0,
        totalBatches: Math.ceil(previewRows.length / EXCEL_SEARCH_BATCH_SIZE),
        canImportRows: 0,
        failedRows: invalidRows.length,
      });
      const completedBatchRows = new Array(
        Math.ceil(previewRows.length / EXCEL_SEARCH_BATCH_SIZE),
      );
      if (previewRows.length > 0) {
        const batches = chunkArray(previewRows, EXCEL_SEARCH_BATCH_SIZE);
        let completedRows = 0;
        let completedBatches = 0;
        const callBatch = async (batchRows) =>
          callJamboProxy(
            "searchOrdersByCommodityBatch",
            {
              rows: batchRows,
              searchBegin,
              searchEnd,
              sortMode,
              mode: "missingQty",
              onlyUnprepared: onlyUnpreparedOrders,
              taskType,
              questionTemplateId:
                taskType === "form"
                  ? selectedCommodity?.formTaskTemplateId ||
                    selectedCommodity?.questionTemplateId ||
                    ""
                  : "",
            },
            currentShop,
          );
        const failedRowFromError = (row, error) => ({
          ...row,
          success: false,
          canImport: false,
          commodity: null,
          matchedRulesCount: 0,
          errors: [`查詢暫時失敗：${error.message || "伺服器忙碌，請稍後重試"}`],
          orders: [],
          selectedOrders: [],
          selectedQty: 0,
          paCount: 0,
          itemQuantityTotal: 0,
        });
        await runWithClientConcurrency(batches, EXCEL_SEARCH_CLIENT_CONCURRENCY, async (batchRows, batchIndex) => {
          let batchResultRows = [];
          try {
            const batchResult = await callBatch(batchRows);
            batchResultRows = batchResult.rows || [];
          } catch (batchError) {
            const retryRows = await runWithClientConcurrency(
              batchRows,
              1,
              async (singleRow) => {
                try {
                  const singleResult = await callBatch([singleRow]);
                  return (singleResult.rows || [])[0] || failedRowFromError(singleRow, batchError);
                } catch (singleError) {
                  return failedRowFromError(singleRow, singleError);
                }
              },
            );
            batchResultRows = retryRows;
          }
          completedBatchRows[batchIndex] = batchResultRows;
          completedRows += batchRows.length;
          completedBatches += 1;
          const nextRows = [
            ...invalidRows,
            ...completedBatchRows.filter(Boolean).flat(),
          ];
          updatePreviewState(nextRows, {
            active: completedBatches < batches.length,
            stage:
              completedBatches < batches.length
                ? `正在查詢直播訂單（第 ${completedBatches}/${batches.length} 批完成）`
                : "Excel 預覽查詢完成",
            completedRows,
            searchableRows: previewRows.length,
            totalRows: rows.length,
            completedBatches,
            totalBatches: batches.length,
            canImportRows: nextRows.filter((row) => row.canImport).length,
            failedRows: nextRows.filter((row) => !row.canImport).length,
          });
        });
      }
      const allRows = [
        ...invalidRows,
        ...completedBatchRows.filter(Boolean).flat(),
      ];
      updatePreviewState(allRows, {
        active: false,
        stage: "Excel 預覽查詢完成",
        completedRows: previewRows.length,
        searchableRows: previewRows.length,
        totalRows: rows.length,
        completedBatches: Math.ceil(previewRows.length / EXCEL_SEARCH_BATCH_SIZE),
        totalBatches: Math.ceil(previewRows.length / EXCEL_SEARCH_BATCH_SIZE),
        canImportRows: allRows.filter((row) => row.canImport).length,
        failedRows: allRows.filter((row) => !row.canImport).length,
      });
      setOrders([]);
      setSelectedOrders([]);
      setSearchSummary(null);
    } catch (error) {
      console.error("缺貨訂單 Excel 預覽失敗:", error);
      alert(`Excel 預覽失敗：${error.message}`);
    } finally {
      setIsLoading(false);
      setExcelPreviewProgress((prev) =>
        prev
          ? {
              ...prev,
              active: false,
            }
          : null,
      );
      event.target.value = "";
    }
  };

  const toggleExcelPreviewRow = (rowKey) => {
    setSelectedExcelRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const importExcelPreviewOrders = async () => {
    const rows = excelPreview?.rows || [];
    if (taskType === "exchange" && Number(selectedCommodity?.targetCount || 0) <= 0) {
      alert("這個商品規則沒有設定可換商品，請先到「商品規則管理」設定後再建立換貨任務。");
      return;
    }
    if (taskType === "form" && !(selectedCommodity?.formTaskTemplateId || selectedCommodity?.questionTemplateId)) {
      alert("這個商品規則沒有設定售後表單，請先到「商品規則管理」設定題目。");
      return;
    }
    const selectedPreviewOrders = rows
      .filter((row) => row.canImport && selectedExcelRowKeys.has(row.rowKey))
      .flatMap((row) => row.selectedOrders || []);
    if (selectedPreviewOrders.length === 0) {
      alert("請先勾選可匯入的 Excel 預覽列");
      return;
    }
    let differenceSetting = {};
    try {
      differenceSetting = taskDifferenceSettingPayload();
    } catch (error) {
      alert(error.message);
      return;
    }
    setIsLoading(true);
    const importBatches = chunkArray(
      selectedPreviewOrders,
      EXCEL_IMPORT_BATCH_SIZE,
    );
    const importTotalBatches = importBatches.length;
    setExcelPreviewProgress({
      active: true,
      stage: "正在分批匯入 Excel 勾選列",
      completedRows: 0,
      searchableRows: selectedPreviewOrders.length,
      totalRows: selectedPreviewOrders.length,
      completedBatches: 0,
      totalBatches: importTotalBatches,
      canImportRows: 0,
      failedRows: 0,
    });
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (taskType === "form") {
        const serviceResult = await createServiceTasksFromOrders(selectedPreviewOrders);
        setExcelPreviewProgress({
          active: false,
          stage: "Excel 表單任務建立完成",
          completedRows: selectedPreviewOrders.length,
          searchableRows: selectedPreviewOrders.length,
          totalRows: selectedPreviewOrders.length,
          completedBatches: 1,
          totalBatches: 1,
          canImportRows: serviceResult.createdCount || 0,
          failedRows: 0,
        });
        alert(`已建立 ${serviceResult.createdCount || 0} 筆售後表單任務。`);
        setExcelPreview(null);
        setSelectedExcelRowKeys(new Set());
        await loadData();
        return;
      }
      let completedRows = 0;
      let completedBatches = 0;
      let createdCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      const updatedDetails = [];
      const skippedDetails = [];
      const batchErrors = [];
      const callImportBatch = async (batchOrders) =>
        callJamboProxy(
          "importJamboExchangeOrders",
          {
            selectedCommodity: {},
            orders: batchOrders,
            ...differenceSetting,
          },
          currentShop,
        );
      const applyResult = (result) => {
        createdCount += Number(result?.createdCount || 0);
        updatedCount += Number(result?.updatedCount || 0);
        skippedCount += Number(result?.skippedCount || 0);
        updatedDetails.push(...(Array.isArray(result?.updated) ? result.updated : []));
        skippedDetails.push(...(Array.isArray(result?.skipped) ? result.skipped : []));
      };
      await runWithClientConcurrency(
        importBatches,
        EXCEL_IMPORT_CLIENT_CONCURRENCY,
        async (batchOrders, batchIndex) => {
          try {
            const result = await callImportBatch(batchOrders);
            applyResult(result);
          } catch (batchError) {
            batchErrors.push(
              `第 ${batchIndex + 1} 批改用單筆重試：${
                batchError.message || "伺服器忙碌"
              }`,
            );
            await runWithClientConcurrency(batchOrders, 1, async (singleOrder) => {
              try {
                const result = await callImportBatch([singleOrder]);
                applyResult(result);
              } catch (singleError) {
                failedCount += 1;
                const paNumber =
                  singleOrder.paNumber || singleOrder.billId || "未知付款單";
                batchErrors.push(
                  `${formatPaNumber(paNumber)} 匯入失敗：${
                    singleError.message || "伺服器忙碌"
                  }`,
                );
              }
            });
          }
          completedRows += batchOrders.length;
          completedBatches += 1;
          setExcelPreviewProgress({
            active: completedBatches < importTotalBatches,
            stage:
              completedBatches < importTotalBatches
                ? `正在匯入 Excel 勾選列（第 ${completedBatches}/${importTotalBatches} 批完成）`
                : "Excel 匯入完成",
            completedRows,
            searchableRows: selectedPreviewOrders.length,
            totalRows: selectedPreviewOrders.length,
            completedBatches,
            totalBatches: importTotalBatches,
            canImportRows: createdCount + updatedCount,
            failedRows: skippedCount + failedCount,
          });
        },
      );
      setExcelPreviewProgress({
        active: false,
        stage: "Excel 匯入完成",
        completedRows: selectedPreviewOrders.length,
        searchableRows: selectedPreviewOrders.length,
        totalRows: selectedPreviewOrders.length,
        completedBatches: importTotalBatches,
        totalBatches: importTotalBatches,
        canImportRows: createdCount + updatedCount,
        failedRows: skippedCount + failedCount,
      });
      alert(
        [
          `已匯入 ${createdCount} 筆換貨資料`,
          updatedCount > 0 ? `更新 ${updatedCount} 筆既有資料` : "",
          skippedCount > 0 ? `略過 ${skippedCount} 筆無法匯入的資料` : "",
          failedCount > 0 ? `失敗 ${failedCount} 筆` : "",
          updatedDetails.length > 0
            ? `已刷新既有資料：${updatedDetails
                .slice(0, 3)
                .map(
                  (item) =>
                    `${formatPaNumber(item.paNumber || "")} ${item.itemNumber || ""} ${item.style || ""}`.trim(),
                )
                .join("；")}`
            : "",
          skippedDetails.length > 0
            ? `略過原因：${skippedDetails
                .slice(0, 3)
                .map(
                  (item) =>
                    `${formatPaNumber(item.paNumber || "")} ${item.itemNumber || ""} ${item.style || ""} ${item.reason || ""}`.trim(),
                )
                .join("；")}`
            : "",
          batchErrors.length > 0
            ? `部分批次已自動重試；${batchErrors.slice(0, 3).join("；")}`
            : "",
        ]
          .filter(Boolean)
          .join("，") + "。",
      );
      setExcelPreview(null);
      setSelectedExcelRowKeys(new Set());
      await loadData();
    } catch (error) {
      alert(`Excel 匯入失敗：${error.message}`);
    } finally {
      setIsLoading(false);
      setExcelPreviewProgress((prev) =>
        prev
          ? {
              ...prev,
              active: false,
            }
          : null,
      );
    }
  };

  const importOrders = async () => {
    if (!selectedCommodity?.commodityId || orders.length === 0) return;
    if (selectedOrders.length === 0) {
      alert("請先勾選要匯入的直播訂單");
      return;
    }
    const commoditiesForImport = selectedCommodities.length > 0 ? selectedCommodities : [selectedCommodity].filter(Boolean);
    if (taskType === "exchange" && commoditiesForImport.some((commodity) => Number(commodity?.targetCount || 0) <= 0)) {
      alert("這個商品規則沒有設定可換商品，請先到「商品規則管理」設定後再建立換貨任務。");
      return;
    }
    if (taskType === "form" && commoditiesForImport.some((commodity) => !(commodity?.formTaskTemplateId || commodity?.questionTemplateId))) {
      alert("這個商品規則沒有設定售後表單，請先到「商品規則管理」設定題目。");
      return;
    }
    let differenceSetting = {};
    try {
      differenceSetting = taskDifferenceSettingPayload();
    } catch (error) {
      alert(error.message);
      return;
    }
    setIsLoading(true);
    try {
      if (taskType === "form") {
        const serviceResult = await createServiceTasksFromOrders(selectedOrders);
        alert(`已建立 ${serviceResult.createdCount || 0} 筆售後表單任務。`);
        setOrders([]);
        setSelectedOrders([]);
        setSearchSummary(null);
        setExcelPreview(null);
        setSelectedExcelRowKeys(new Set());
        await loadData();
        return;
      }
      const result = await callJamboProxy(
        "importJamboExchangeOrders",
        {
          selectedCommodity,
          orders: selectedOrders,
          ...differenceSetting,
        },
        currentShop,
      );
      const createdCount = result.createdCount || 0;
      const updatedCount = result.updatedCount || 0;
      const skippedCount = result.skippedCount || 0;
      const updatedDetails = Array.isArray(result.updated) ? result.updated : [];
      const skippedDetails = Array.isArray(result.skipped) ? result.skipped : [];
      const importMessage = [
        `已匯入 ${createdCount} 筆換貨資料`,
        updatedCount > 0 ? `更新 ${updatedCount} 筆既有資料` : "",
        skippedCount > 0 ? `略過 ${skippedCount} 筆無法匯入的資料` : "",
        updatedDetails.length > 0
          ? `已刷新既有資料：${updatedDetails
              .slice(0, 3)
              .map(
                (item) =>
                  `${formatPaNumber(item.paNumber || "")} ${item.itemNumber || ""} ${item.style || ""}`.trim(),
              )
              .join("；")}`
          : "",
        skippedDetails.length > 0
          ? `略過原因：${skippedDetails
              .slice(0, 3)
              .map(
                (item) =>
                  `${formatPaNumber(item.paNumber || "")} ${item.itemNumber || ""} ${item.style || ""} ${item.reason || ""}`.trim(),
              )
              .join("；")}`
          : "",
      ].filter(Boolean).join("，");
      alert(`${importMessage}。`);
      setOrders([]);
      setSelectedOrders([]);
      setSearchSummary(null);
      setExcelPreview(null);
      setSelectedExcelRowKeys(new Set());
      await loadData();
    } catch (error) {
      alert(`匯入失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const differenceRewardInputFor = (item = {}) => {
    if (Object.prototype.hasOwnProperty.call(differenceRewardInputs, item.id)) {
      return differenceRewardInputs[item.id];
    }
    if (item.exchangeDifferenceRefundAmountSet === true) {
      return String(item.exchangeDifferenceRefundAmountInput ?? item.exchangeDifferenceRefundAmount ?? 0);
    }
    return "";
  };

  const manualDifferencePayloadFor = (item = {}) => {
    const jobId = item.jamboExchangeJobId || item.id;
    if (item.customerSelection?.refundPriceDifference !== true) return { jobId };
    const raw = String(differenceRewardInputFor(item)).trim();
    if (raw === "") throw new Error("請輸入差額紅利點數；沒有差額請輸入 0");
    const amount = Number(raw);
    if (!Number.isInteger(amount) || amount < 0 || amount > 1000000) {
      throw new Error("差額紅利請輸入 0 到 1,000,000 的整數點數");
    }
    return {
      jobId,
      differenceRewardAmount: amount,
      differenceRewardAmountSet: true,
    };
  };

  const enqueueAndRun = async (item) => {
    setIsLoading(true);
    try {
      const enqueue = adminCallable("tianlanEnqueueJamboExchangeForPa");
      await enqueue(withShopScope({ paNumber: item.paNumber }, currentShop));
      const run = adminCallable("tianlanRunJamboExchangeJob");
      const response = await run(withShopScope({
        paNumber: item.paNumber,
        ...manualDifferencePayloadFor(item),
      }, currentShop));
      await loadData();
      const completionError = response.data?.completion?.success === false
        ? response.data.completion.error
        : "";
      alert(completionError
        ? `換貨已成功，但退點／完成簡訊尚未完成：${completionError}`
        : "換貨已成功驗證；差額紅利與完成簡訊會依設定自動處理。");
    } catch (error) {
      alert(`執行失敗：${error.message}`);
      await loadData();
    }
    setIsLoading(false);
  };

  const retryExchangeCompletion = async (item) => {
    setIsLoading(true);
    try {
      const retry = adminCallable("tianlanRetryJamboExchangeCompletion");
      await retry(withShopScope({
        paNumber: item.paNumber,
        ...manualDifferencePayloadFor(item),
      }, currentShop));
      await loadData();
      alert("已重新處理差額紅利與換貨完成簡訊。");
    } catch (error) {
      alert(`完成後處理失敗：${error.message}`);
      await loadData();
    }
    setIsLoading(false);
  };

  const openDifferenceTaskEditor = (item) => {
    const enabled = item.refundPriceDifference === true || item.customerSelection?.refundPriceDifference === true;
    setDifferenceTaskDialog({
      open: true,
      itemId: item.id,
      paNumber: item.paNumber || "",
      enabled,
      amount: enabled
        ? String(item.exchangeDifferenceRefundAmountInput ?? item.exchangeDifferenceRefundAmount ?? 0)
        : "",
    });
  };

  const saveDifferenceTaskEditor = async () => {
    const raw = String(differenceTaskDialog.amount).trim();
    const amount = Number(raw);
    if (differenceTaskDialog.enabled && (raw === "" || !Number.isInteger(amount) || amount < 0 || amount > 1000000)) {
      alert("差額紅利請輸入 0 到 1,000,000 的整數點數");
      return;
    }
    setIsLoading(true);
    try {
      const updateDifference = adminCallable("tianlanUpdateExchangeTaskDifference");
      await updateDifference(withShopScope({
        exchangeDataId: differenceTaskDialog.itemId,
        refundPriceDifference: differenceTaskDialog.enabled,
        differenceRewardAmount: differenceTaskDialog.enabled ? amount : 0,
      }, currentShop));
      setDifferenceTaskDialog({ open: false, itemId: "", paNumber: "", enabled: false, amount: "" });
      await loadData();
      alert(differenceTaskDialog.enabled ? `已設定退還差額紅利 ${amount} 點。` : "已關閉這筆任務的差額紅利。");
    } catch (error) {
      alert(`更新任務失敗：${error.message}`);
      await loadData();
    }
    setIsLoading(false);
  };

  const runSelectedExchanges = async () => {
    const selectedItems = exchangeItems.filter(
      (item) =>
        selectedExchangeIds.includes(item.id) &&
        item.customerSelection?.action === "exchange" &&
        item.paNumber,
    );
    if (selectedItems.length === 0) {
      alert("請先勾選「客人已選，待換貨」的資料");
      return;
    }
    try {
      selectedItems.forEach((item) => manualDifferencePayloadFor(item));
    } catch (error) {
      alert(error.message);
      return;
    }
    setIsLoading(true);
    try {
      const enqueue = adminCallable("tianlanEnqueueJamboExchangeForPa");
      const run = adminCallable("tianlanRunJamboExchangeJob");
      for (const item of selectedItems) {
        await enqueue(withShopScope({ paNumber: item.paNumber }, currentShop));
        await run(withShopScope({
          paNumber: item.paNumber,
          ...manualDifferencePayloadFor(item),
        }, currentShop));
      }
      setSelectedExchangeIds([]);
      await loadData();
      alert(`已依序處理 ${selectedItems.length} 筆換貨`);
    } catch (error) {
      alert(`執行勾選換貨失敗：${error.message}`);
      await loadData();
    }
    setIsLoading(false);
  };

  const removeExchangeItem = async (item) => {
    const displayPaNumber =
      item.paNumber || item.jamboOrderId || item.orderId || "-";
    setRemoveDialog({
      open: true,
      ids: [item.id],
      target: "exchange",
      title: "移除這筆換貨資料",
      message: `確定要移除付款單 ${formatPaNumber(displayPaNumber)} 這筆換貨資料嗎？相關換貨工作也會一起移除。`,
    });
  };

  const removeSelectedExchangeItems = async () => {
    const ids = selectedExchangeIds.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (ids.length === 0) {
      alert("請先勾選要移除的換貨資料");
      return;
    }
    setRemoveDialog({
      open: true,
      ids,
      target: "exchange",
      title: `移除已選擇的 ${ids.length} 筆資料`,
      message: `確定要移除已選擇的 ${ids.length} 筆換貨資料嗎？相關換貨工作也會一起移除。`,
    });
  };

  const removeServiceTask = (task) => {
    const displayPaNumber = task.paNumber || task.billId || task.orderId || "-";
    setRemoveDialog({
      open: true,
      ids: [task.id],
      target: "service",
      title: "移除這筆表單任務",
      message: `確定要移除付款單 ${formatPaNumber(displayPaNumber)} 這筆表單任務嗎？移除後客人前台不會再看到這張表單。`,
    });
  };

  const removeSelectedServiceTasks = () => {
    const ids = selectedServiceTaskIds.filter((id) =>
      serviceTasks.some((task) => task.id === id && task.taskType !== "exchange"),
    );
    if (ids.length === 0) {
      alert("請先勾選要移除的表單任務");
      return;
    }
    setRemoveDialog({
      open: true,
      ids,
      target: "service",
      title: `移除已選擇的 ${ids.length} 筆表單任務`,
      message: `確定要移除已選擇的 ${ids.length} 筆表單任務嗎？移除後客人前台不會再看到這些表單。`,
    });
  };

  const confirmRemoveItems = async () => {
    const target = removeDialog.target || "exchange";
    const sourceItems = target === "service" ? serviceTasks : exchangeItems;
    const ids = removeDialog.ids.filter((id) =>
      sourceItems.some((item) => item.id === id),
    );
    if (ids.length === 0) {
      setRemoveDialog({ open: false, ids: [], target: "exchange", title: "", message: "" });
      alert(target === "service"
        ? "找不到可移除的表單任務，請重新整理後再試一次"
        : "找不到可移除的換貨資料，請重新整理後再試一次");
      return;
    }
    setIsLoading(true);
    try {
      const remove = adminCallable(target === "service" ? "tianlanDeleteServiceTaskBatch" : "tianlanDeleteJamboExchangeItem");
      const payload = target === "service"
        ? (ids.length === 1 ? { taskId: ids[0] } : { taskIds: ids })
        : (ids.length === 1 ? { exchangeDataId: ids[0] } : { exchangeDataIds: ids });
      const result = await remove(withShopScope(payload, currentShop));
      setRemoveDialog({ open: false, ids: [], target: "exchange", title: "", message: "" });
      if (target === "service") {
        setSelectedServiceTaskIds((prev) => prev.filter((id) => !ids.includes(id)));
      } else {
        setSelectedExchangeIds([]);
      }
      await loadData();
      const deletedItems = Number(result.data?.deletedItems ?? 0) || 0;
      const missingItems = Number(result.data?.missingItems ?? 0) || 0;
      alert(
        missingItems > 0
          ? `已移除 ${deletedItems} 筆${target === "service" ? "表單任務" : "換貨資料"}，另有 ${missingItems} 筆已不存在或未找到，畫面已重新整理。`
          : `已移除 ${deletedItems} 筆${target === "service" ? "表單任務" : "換貨資料"}`,
      );
    } catch (error) {
      alert(`移除失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const batchUpdateServiceStatus = async (action) => {
    if (selectedExchangeIds.length === 0) {
      alert("請先勾選要處理的付款單");
      return;
    }
    if (action === "pending") {
      openReturnDialog(selectedExchangeIds);
      return;
    }
    setIsLoading(true);
    try {
      const updateStatus = adminCallable("tianlanUpdateJamboExchangeServiceStatus");
      await updateStatus(
        withShopScope(
          { exchangeDataIds: selectedExchangeIds, action },
          currentShop,
        ),
      );
      setSelectedExchangeIds([]);
      await loadData();
      alert(
        action === "handled"
          ? "已標記已處理"
          : "已退回待客人選擇",
      );
    } catch (error) {
      alert(`批次更新失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const batchMarkSellerOutOfStock = async () => {
    const ids = selectedSellerOutOfStockItems.map((item) => item.id);
    if (ids.length === 0) {
      alert("請先勾選待客人選擇或已過期的換貨資料");
      return;
    }
    setIsLoading(true);
    try {
      const updateStatus = adminCallable("tianlanUpdateJamboExchangeServiceStatus");
      const result = await updateStatus(
        withShopScope(
          {
            exchangeDataIds: ids,
            action: "out_of_stock",
            reason: "賣家斷貨，改列不換貨退款",
          },
          currentShop,
        ),
      );
      setSelectedExchangeIds((prev) => prev.filter((id) => !ids.includes(id)));
      await loadData();
      const skipped = selectedExchangeIds.length - ids.length;
      alert(
        skipped > 0
          ? `已標記 ${result.data?.updated || ids.length} 筆賣家斷貨，另略過 ${skipped} 筆已選擇或不可斷貨資料`
          : `已標記 ${result.data?.updated || ids.length} 筆賣家斷貨`,
      );
    } catch (error) {
      alert(`批次賣家斷貨失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openStockoutRefundDialog = () => {
    const ids = selectedStockoutRefundItems.map((item) => item.id);
    if (ids.length === 0) {
      alert("請先勾選不換貨申請退款或賣家斷貨的資料");
      return;
    }
    setStockoutRefundDialog({ open: true, ids });
  };

  const confirmExecuteStockoutRefund = async () => {
    const ids = stockoutRefundDialog.ids.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (ids.length === 0) {
      setStockoutRefundDialog({ open: false, ids: [] });
      alert("找不到可執行斷貨退紅利的資料，請重新整理後再試一次");
      return;
    }
    setIsLoading(true);
    try {
      const executeRefund = adminCallable("tianlanExecuteJamboStockoutRefundBatch");
      const result = await executeRefund(
        withShopScope(
          { exchangeDataIds: ids },
          currentShop,
        ),
      );
      setStockoutRefundDialog({ open: false, ids: [] });
      setSelectedExchangeIds((prev) => prev.filter((id) => !ids.includes(id)));
      await loadData();
      alert(
        `已執行斷貨退紅利 ${result.data?.updated || 0} 筆` +
          (result.data?.failed ? `，失敗 ${result.data.failed} 筆` : ""),
      );
    } catch (error) {
      alert(`執行斷貨退紅利失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openReturnDialog = (ids = []) => {
    const validIds = ids.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (validIds.length === 0) {
      alert("請先選擇要退回的換貨資料");
      return;
    }
    const selectedItems = exchangeItems.filter((item) => validIds.includes(item.id));
    const restoreCount = selectedItems.filter(
      (item) =>
        item.customerSelection?.action === "exchange" &&
        (item.status === "exchanged" ||
          item.jamboExchangeStatus === "success" ||
          item.jobStatus === "success"),
    ).length;
    setReturnDialog({
      open: true,
      ids: validIds,
      mode: "return",
      title: validIds.length === 1 ? "退回給客人重新選擇" : `退回 ${validIds.length} 筆資料重新選擇`,
      message:
        restoreCount > 0
          ? `其中 ${restoreCount} 筆已完成直播換貨，退回前會先把直播訂單換回原本缺貨商品。確定要退回並還原嗎？`
          : "這些資料會清除客人目前選擇並退回待選擇狀態。確定要退回嗎？",
    });
  };

  const openCancelHandledDialog = (ids = []) => {
    const validIds = ids.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (validIds.length === 0) {
      alert("請先選擇要取消已處理標記的資料");
      return;
    }
    setReturnDialog({
      open: true,
      ids: validIds,
      mode: "cancelHandled",
      title:
        validIds.length === 1
          ? "取消標記已處理"
          : `取消 ${validIds.length} 筆已處理標記`,
      message:
        "取消後資料會回到原本的處理狀態，已處理時扣掉的保留庫存會加回，不會清除客人的選擇，也不會操作直播訂單。",
    });
  };

  const confirmReturnExchangeItems = async () => {
    const ids = returnDialog.ids.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (ids.length === 0) {
      setReturnDialog({ open: false, ids: [], title: "", message: "", mode: "return" });
      alert("找不到可退回的換貨資料，請重新整理後再試一次");
      return;
    }
    const isCancelHandled = returnDialog.mode === "cancelHandled";
    setIsLoading(true);
    try {
      const updateStatus = adminCallable("tianlanUpdateJamboExchangeServiceStatus");
      const result = await updateStatus(
        withShopScope(
          {
            exchangeDataIds: ids,
            action: "pending",
            restoreOriginalBeforePending: !isCancelHandled,
          },
          currentShop,
        ),
      );
      setReturnDialog({ open: false, ids: [], title: "", message: "", mode: "return" });
      setSelectedExchangeIds((prev) => prev.filter((id) => !ids.includes(id)));
      await loadData();
      const restoredCount = (result.data?.restoreResults || []).filter(
        (item) => item.success,
      ).length;
      alert(
        isCancelHandled
          ? "已取消標記已處理，資料已回到原本的處理狀態"
          : restoredCount > 0
          ? `已還原 ${restoredCount} 筆直播訂單，並退回給客人重新選擇`
          : "已退回，客人可以重新選擇換貨商品",
      );
    } catch (error) {
      alert(`退回失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const returnExchangeItemToCustomer = async (item) => {
    if (!item?.id) return;
    openReturnDialog([item.id]);
  };

  const repairExchangeItemStyle = async (item) => {
    if (!item?.id) return;
    setIsLoading(true);
    try {
      const repairStyle = adminCallable("tianlanRepairJamboExchangeOriginalStyle");
      const result = await repairStyle(
        withShopScope({ exchangeDataId: item.id }, currentShop),
      );
      await loadData();
      const firstResult = result.data?.results?.[0] || {};
      if (firstResult.success) {
        alert(
          firstResult.skipped
            ? "這筆資料的樣式已經匹配規則"
            : `已修復樣式：${firstResult.previousStyle || "-"} → ${firstResult.repairedStyle || firstResult.fetchedStyle || "-"}`,
        );
      } else {
        alert(`修復失敗：${firstResult.error || "直播訂單沒有可用樣式"}`);
      }
    } catch (error) {
      alert(`修復失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const repairSelectedExchangeItemStyles = async () => {
    const selectedItems = exchangeItems.filter(
      (item) =>
        selectedExchangeIds.includes(item.id) &&
        isStyleMismatchExchangeItem(item, exchangeRules),
    );
    if (selectedItems.length === 0) {
      alert("請先勾選需要修復的異常單");
      return;
    }
    setIsLoading(true);
    try {
      const repairStyle = adminCallable("tianlanRepairJamboExchangeOriginalStyle");
      const batches = chunkArray(
        selectedItems.map((item) => item.id),
        10,
      );
      const results = [];
      for (const ids of batches) {
        const result = await repairStyle(
          withShopScope({ exchangeDataIds: ids }, currentShop),
        );
        results.push(...(result.data?.results || []));
      }
      await loadData();
      const updated = results.filter((item) => item.success && !item.skipped).length;
      const skipped = results.filter((item) => item.success && item.skipped).length;
      const failed = results.filter((item) => !item.success).length;
      const firstError = results.find((item) => !item.success)?.error;
      alert(
        failed > 0
          ? `已修復 ${updated} 筆，${skipped} 筆已匹配，${failed} 筆未修復。${firstError ? `原因：${firstError}` : ""}`
          : `已修復 ${updated} 筆樣式，${skipped} 筆原本已匹配規則`,
      );
    } catch (error) {
      alert(`批次修復失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const markExchangeItemHandled = async (item) => {
    if (!item?.id) return;
    setIsLoading(true);
    try {
      const updateStatus = adminCallable("tianlanUpdateJamboExchangeServiceStatus");
      await updateStatus(
        withShopScope(
          { exchangeDataIds: [item.id], action: "handled" },
          currentShop,
        ),
      );
      setSelectedExchangeIds((prev) => prev.filter((id) => id !== item.id));
      await loadData();
      alert("已標記為已處理");
    } catch (error) {
      alert(`標記失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const updateAutoRunMode = async (enabled) => {
    setIsLoading(true);
    try {
      await setDoc(
        doc(
          db,
          "tianlan_settings",
          getScopedSettingsDocId("messages", currentShop),
        ),
        withShopScope(
          {
            jamboAutoRun: enabled,
            updatedAt: new Date().toISOString(),
          },
          currentShop,
        ),
        { merge: true },
      );
      setAutoRunSettings({ jamboAutoRun: enabled, loaded: true });
      alert(enabled ? "已開啟全自動換貨" : "已切換為人工確認換貨");
    } catch (error) {
      alert(`更新換貨模式失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const buildSelectedServiceSmsPayload = (excludeSent = true) => {
    const selectedIdSet = new Set(selectedServiceTaskIds);
    const selectedItems = serviceTasks.filter((item) =>
      selectedIdSet.has(item.id) &&
      item.taskType !== "exchange" &&
      (!excludeSent || !["sent", "queued", "sending"].includes(item.formSmsStatus)),
    );
    const ids = selectedItems.map((item) => item.id).filter(Boolean);
    const serviceTasksForSms = selectedItems.map((item) => {
      const template = serviceTemplates.find((templateItem) => templateItem.id === (item.questionTemplateId || item.formId)) || {};
      return {
        id: item.id,
        paNumber: item.paNumber || item.billId || item.jamboOrderId || "",
        billId: item.billId || "",
        jamboOrderItemId: item.jamboOrderItemId || item.orderId || "",
        orderId: item.orderId || item.jamboOrderItemId || "",
        fromName: item.fromName || "",
        recipientPhone: item.recipientPhone || item.phone || "",
        serviceDeadline: item.serviceDeadline || item.exchangeDeadline || "",
        exchangeDeadline: item.serviceDeadline || item.exchangeDeadline || "",
        formSmsStatus: item.formSmsStatus || "",
        quantity: Number(item.quantity || item.takeQty || 1) || 1,
        formTitle: item.formTitle || template.title || template.formTitle || "",
        product: {
          itemNumber: item.product?.itemNumber || item.product?.partNumber || "",
          name: item.product?.name || "",
        },
        originalProduct: {
          itemNumber: item.originalProduct?.itemNumber || item.product?.itemNumber || item.product?.partNumber || "",
          name: item.originalProduct?.name || item.product?.name || "",
        },
        originalStyle: item.originalStyle || "",
      };
    });
    return {
      ids,
      serviceTasksForSms,
      exchangeItemsForSms: [],
      excludedSentCount: selectedServiceTaskIds.length - selectedItems.length,
    };
  };

  const buildSelectedSmsPayload = (excludeSent = true, smsKind = smsDialog.smsKind || "exchange") => {
    if (smsKind === "form") return buildSelectedServiceSmsPayload(excludeSent);
    const selectedIdSet = new Set(selectedExchangeIds);
    const statusField = smsKind === "stockout" ? "stockoutSmsStatus" : "smsStatus";
    const selectedItems = exchangeItems.filter((item) =>
      selectedIdSet.has(item.id) &&
      (smsKind !== "stockout" ||
        item.customerSelection?.action === "cancel" ||
        ["refund_requested", "out_of_stock"].includes(item.status)) &&
      (!excludeSent || !["sent", "queued", "sending"].includes(item[statusField])),
    );
    const ids = selectedItems.map((item) => item.id).filter(Boolean);
    const exchangeItemsForSms = selectedItems.map((item) => ({
      id: item.id,
      paNumber: item.paNumber || item.billId || item.jamboOrderId || "",
      billId: item.billId || "",
      jamboOrderItemId: item.jamboOrderItemId || item.orderId || "",
      orderId: item.orderId || item.jamboOrderItemId || "",
      fromName: item.fromName || "",
      recipientPhone: item.recipientPhone || item.phone || "",
      exchangeDeadline: item.exchangeDeadline || "",
      smsStatus: item[statusField] || "",
      stockoutSmsStatus: item.stockoutSmsStatus || "",
      quantity: selectionQuantityOf(item),
      originalProduct: {
        itemNumber: item.originalProduct?.itemNumber || "",
        name: item.originalProduct?.name || "",
      },
      originalStyle: item.originalStyle || "",
    }));
    return { ids, exchangeItemsForSms, serviceTasksForSms: [], excludedSentCount: selectedExchangeIds.length - selectedItems.length };
  };

  const prepareSmsBatch = async () => {
    const smsKind = smsDialog.smsKind || "exchange";
    const { ids, exchangeItemsForSms, serviceTasksForSms, excludedSentCount } = buildSelectedSmsPayload(smsDialog.excludeSent, smsKind);
    if (ids.length === 0) {
      alert(smsDialog.excludeSent && excludedSentCount > 0
        ? `勾選資料都已發送過${smsKind === "stockout" ? "斷貨" : smsKind === "form" ? "表單" : "換貨"}簡訊，若要重發請取消「排除已發送」。`
        : `請先勾選要產生${smsKind === "stockout" ? "斷貨" : smsKind === "form" ? "表單" : "換貨"}簡訊的資料`);
      return;
    }
    setSmsDialog((prev) => ({
      ...prev,
      open: true,
      preparing: true,
      quickConfirm: null,
      messages: [],
      copied: false,
      sentResults: [],
    }));
    try {
      const prepareSms = adminCallable("tianlanPrepareExchangeSmsBatch");
      const response = await prepareSms(
        withShopScope(
          {
            exchangeDataIds: smsKind === "form" ? [] : ids,
            serviceTaskIds: smsKind === "form" ? ids : [],
            exchangeItems: exchangeItemsForSms,
            serviceTasks: serviceTasksForSms,
            excludeSent: smsDialog.excludeSent,
            sendFbMessenger: smsDialog.sendFbMessenger,
            smsKind,
            smsAccessLinkDays,
            exchangeUrlBase: `${window.location.origin}/tianlan/customer`,
          },
          currentShop,
        ),
      );
      setSmsDialog((prev) => ({
        ...prev,
        open: true,
        preparing: false,
        messages: response.data?.messages || [],
        copied: false,
        sentResults: [],
      }));
      return response.data?.messages || [];
    } catch (error) {
      setSmsDialog((prev) => ({ ...prev, preparing: false }));
      alert(`產生${smsKind === "stockout" ? "斷貨" : smsKind === "form" ? "表單" : "換貨"}簡訊失敗：${error.message}`);
    }
  };

  const quickQueueSmsBatch = async () => {
    const messages = await prepareSmsBatch();
    if (!messages?.length) return;
    setSmsDialog((prev) => ({ ...prev, quickConfirm: {
      recipientCount: messages.length,
      uniquePaCount: new Set(messages.flatMap((message) => message.paNumbers || [message.paNumber])).size,
    } }));
  };

  const openDeadlineDialog = (target) => {
    const ids = new Set(target === "service" ? selectedServiceTaskIds : selectedExchangeIds);
    const items = (target === "service" ? serviceTasks : exchangeItems).filter((item) => ids.has(item.id));
    const summary = summarizeDeadlines(items);
    setDeadlineDialog({ open: true, target, deadline: summary.input, ...summary });
  };

  const setSelectedDeadlineBatch = async (clearDeadline = false) => {
    const ids = selectedExchangeIds.filter((id) =>
      exchangeItems.some((item) => item.id === id),
    );
    if (ids.length === 0) {
      alert("請先勾選要設定期限的換貨資料");
      return;
    }
    if (!clearDeadline && !deadlineDialog.deadline) {
      alert("請先選擇截止時間");
      return;
    }
    setIsLoading(true);
    try {
      const setDeadline = adminCallable("tianlanSetJamboExchangeDeadlineBatch");
      const response = await setDeadline(
        withShopScope(
          {
            exchangeDataIds: ids,
            exchangeDeadline: clearDeadline ? null : new Date(`${deadlineDialog.deadline}:00+08:00`).toISOString(),
            clearDeadline,
            deadlineSource: "manual",
          },
          currentShop,
        ),
      );
      setDeadlineDialog((prev) => ({ ...prev, open: false }));
      await loadData();
      alert(`已${clearDeadline ? "解除" : "設定"} ${response.data?.updated ?? ids.length} 筆換貨期限`);
    } catch (error) {
      alert(`設定換貨期限失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const sendSmsBatch = async () => {
    const smsLogIds = smsDialog.messages
      .map((message) => message.smsLogId || message.id)
      .filter(Boolean);
    if (smsLogIds.length === 0) {
      alert("請先產生簡訊預覽");
      return;
    }
    setSmsDialog((prev) => ({ ...prev, sending: true, quickConfirm: null }));
    try {
      const sendSms = adminCallable("tianlanSendExchangeSmsBatch");
      const response = await sendSms(
        withShopScope(
          {
            smsLogIds,
            excludeSent: smsDialog.excludeSent,
            sendFbMessenger: smsDialog.sendFbMessenger,
          },
          currentShop,
        ),
      );
      const results = response.data?.results || [];
      setSmsDialog((prev) => ({
        ...prev,
        sending: false,
        sentResults: results,
        messages: prev.messages.map((message) => {
          const id = message.smsLogId || message.id;
          const result = results.find((item) => item.smsLogId === id);
          return result ? { ...message, status: result.success ? "queued" : "failed", error: result.error || "" } : message;
        }),
      }));
      alert(`已建立簡訊發送任務：${response.data?.queuedCount || 0} 則，失敗 ${response.data?.failedCount || 0} 則。成功發送後處理清單會標示已發送簡訊。`);
      await loadData();
    } catch (error) {
      setSmsDialog((prev) => ({ ...prev, sending: false }));
      alert(`發送簡訊失敗：${error.message}`);
    }
  };

  const copySmsMessages = async () => {
    const text = smsDialog.messages
      .map((message) => `${(message.paNumbers || [message.paNumber]).join("、")} ${message.phone || ""}\n${message.message}`)
      .join("\n\n");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setSmsDialog((prev) => ({ ...prev, copied: true }));
    } catch (error) {
      alert(`複製失敗：${error.message}`);
    }
  };

  const dashboardEntries = [
    ...exchangeItems.map((item) => ({ kind: "exchange", item })),
    ...serviceTasks
      .filter((task) => task.taskType !== "exchange")
      .map((item) => ({ kind: "service", item })),
  ];
  const dashboardCustomerKey = createCustomerCountKey(dashboardEntries.map(({ item }) => item));
  const dashboardCounts = dashboardEntries.reduce(
    (acc, entry) => {
      const item = entry.item;
      const recordCustomer = (stage) => {
        const key = dashboardCustomerKey(item);
        if (key) acc.customers[stage].add(key);
        else acc.unidentified[stage] += 1;
      };
      if (entry.kind === "service") {
        const label = serviceTaskLabelOf(item);
        if (label === "待客人填寫") { acc.waitingCustomer += 1; recordCustomer("waitingCustomer"); }
        else if (label === "客人已送出") { acc.readyToExchange += 1; recordCustomer("readyToExchange"); }
        else if (label === "已處理") acc.completed += 1;
        else if (label === "已過期") acc.needsHelp += 1;
        return acc;
      }
      const itemJobs = jobs.filter(
        (job) =>
          job.exchangeDataId === item.id ||
          (item.paNumber && job.paNumber === item.paNumber),
      );
      const label = statusLabelOf(item, itemJobs);
      if (itemJobs.some((job) => job.status === "running")) {
        acc.running += 1;
      } else if (label === "待客人選擇") {
        acc.waitingCustomer += 1;
        recordCustomer("waitingCustomer");
      } else if (label === "客人已選，待換貨") {
        acc.readyToExchange += 1;
        recordCustomer("readyToExchange");
      } else if (label === "已換貨" || label === "已處理") {
        acc.completed += 1;
      } else if (
        label.includes("失敗") ||
        label.includes("還原") ||
        label.includes("人工")
      ) {
        acc.needsHelp += 1;
      }
      return acc;
    },
    {
      customers: { waitingCustomer: new Set(), readyToExchange: new Set() },
      unidentified: { waitingCustomer: 0, readyToExchange: 0 },
      waitingCustomer: 0,
      readyToExchange: 0,
      running: 0,
      completed: 0,
      needsHelp: 0,
    },
  );
  const selectedOrderIds = new Set(
    selectedOrders.map((order) => String(order.jamboOrderItemId)),
  );
  const commodityStyles =
    Array.isArray(selectedCommodity?.selectedStyles) &&
    selectedCommodity.selectedStyles.length > 0
      ? selectedCommodity.selectedStyles
      : styleLabelsOf(selectedCommodity);
  const ruleSearchProducts = useMemo(() => {
    const productsById = new Map();
    exchangeRules
      .filter((rule) => rule.enabled !== false)
      .forEach((rule) => {
        const sourceProducts = Array.isArray(rule.sourceProducts)
          ? rule.sourceProducts
          : [];
        const sourceIds = Array.isArray(rule.sourceCommodityIds)
          ? rule.sourceCommodityIds
          : [rule.sourceCommodityId].filter(Boolean);
        const targets = Array.isArray(rule.targetProducts)
          ? rule.targetProducts
          : [];

        sourceProducts.forEach((product) => {
          const id = commodityIdOf(product);
          if (!id) return;
          const combinedSettings = normalizeCombinedFormSettings(product);
          const current = productsById.get(id) || {
            commodityId: id,
            id,
            partNumber: product.partNumber || product.itemNumber || "",
            name: product.name || "",
            image: product.image || "",
            price: product.price || 0,
            styles: [],
            selectedStyles: [],
            ruleIds: [],
            targetCount: 0,
            allowCancelRefund: false,
            formTaskTemplateId: "",
            questionTemplateId: "",
            formTaskTemplateTitle: "",
            formResponsePolicy: product.formResponsePolicy || rule.formResponsePolicy || { mode: "per_unit", quantityPerResponse: 1 },
            formChildResponsePolicies: product.formChildResponsePolicies || {},
            ruleTaskType: "",
            combined: combinedSettings.combined,
            subCommodities: combinedSettings.subCommodities,
            formProductMode: combinedSettings.formProductMode,
            selectedFormChildKeys: combinedSettings.selectedFormChildKeys,
          };
          const styles = styleLabelsOf(product);
          const selectedStyles =
            Array.isArray(product.selectedStyles) &&
            product.selectedStyles.length > 0
              ? product.selectedStyles
              : styles;
          productsById.set(id, {
            ...current,
            partNumber:
              current.partNumber ||
              product.partNumber ||
              product.itemNumber ||
              "",
            name: current.name || product.name || "",
            image: current.image || product.image || "",
            price: current.price || product.price || 0,
            styles: mergeUnique([...current.styles, ...styles]),
            selectedStyles: mergeUnique([
              ...current.selectedStyles,
              ...selectedStyles,
            ]),
            ruleIds: mergeUnique([...current.ruleIds, rule.id]),
            targetCount: current.targetCount + targets.length,
            allowCancelRefund:
              current.allowCancelRefund || rule.allowCancelRefund === true,
            formTaskTemplateId:
              current.formTaskTemplateId || rule.formTaskTemplateId || rule.questionTemplateId || "",
            questionTemplateId:
              current.questionTemplateId || rule.questionTemplateId || rule.formTaskTemplateId || "",
            formTaskTemplateTitle:
              current.formTaskTemplateTitle || rule.formTaskTemplateTitle || "",
            formResponsePolicy:
              product.formResponsePolicy || rule.formResponsePolicy || current.formResponsePolicy,
            formChildResponsePolicies:
              Object.keys(product.formChildResponsePolicies || {}).length > 0
                ? product.formChildResponsePolicies
                : current.formChildResponsePolicies || {},
            combined: current.combined || combinedSettings.combined,
            subCommodities: current.subCommodities?.length > 0
              ? current.subCommodities
              : combinedSettings.subCommodities,
            formProductMode: product.formProductMode === "children"
              ? "children"
              : current.formProductMode || "combined",
            selectedFormChildKeys: product.formProductMode === "children"
              ? combinedSettings.selectedFormChildKeys
              : current.selectedFormChildKeys || combinedSettings.selectedFormChildKeys,
            ruleTaskType:
              current.ruleTaskType ||
              rule.ruleTaskType ||
              (targets.length > 0 ? "exchange" : "form"),
          });
        });

        sourceIds.forEach((sourceId) => {
          const id = String(sourceId || "");
          if (!id || productsById.has(id)) return;
          productsById.set(id, {
            commodityId: id,
            id,
            partNumber: "",
            name: "已建立規則商品",
            image: "",
            price: 0,
            styles: [],
            selectedStyles: [],
            ruleIds: [rule.id],
            targetCount: targets.length,
            allowCancelRefund: rule.allowCancelRefund === true,
            formTaskTemplateId: rule.formTaskTemplateId || rule.questionTemplateId || "",
            questionTemplateId: rule.questionTemplateId || rule.formTaskTemplateId || "",
            formTaskTemplateTitle: rule.formTaskTemplateTitle || "",
            formResponsePolicy: rule.formResponsePolicy || { mode: "per_unit", quantityPerResponse: 1 },
            ruleTaskType: rule.ruleTaskType || (targets.length > 0 ? "exchange" : "form"),
          });
        });
      });

    return Array.from(productsById.values()).sort((a, b) =>
      String(a.partNumber || a.name || a.commodityId).localeCompare(
        String(b.partNumber || b.name || b.commodityId),
        "zh-Hant",
      ),
    );
  }, [exchangeRules]);
  const filteredRuleSearchProducts = ruleSearchProducts.filter((product) => {
    const keyword = ruleKeyword.trim().toLowerCase();
    if (!keyword) return true;
    return [
      product.partNumber,
      product.name,
      product.commodityId,
      ...(product.selectedStyles || []),
      ...(product.styles || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(keyword);
  });
  const jobsByExchangeId = jobs.reduce((acc, job) => {
    const key = job.exchangeDataId || job.paNumber;
    if (!key) return acc;
    acc[key] = acc[key] || [];
    acc[key].push(job);
    return acc;
  }, {});
  const queueTabs = [
    { id: "all", label: "全部" },
    { id: "pending", label: "待客人處理" },
    { id: "filled", label: "客人已送出" },
    { id: "abnormal", label: "需協助" },
    { id: "handled", label: "已處理" },
  ];
  const queueMoreFilters = [
    { id: "expired", label: "已過期" },
    { id: "exchanged", label: "已完成換貨" },
    { id: "failed", label: "換貨失敗" },
    { id: "restore_failed", label: "價格還原失敗" },
    { id: "refund", label: "退款 / 斷貨" },
  ];
  const statusMatchesFilter = (item, itemJobs) => {
    if (serviceFilter === "all") return true;
    const label = statusLabelOf(item, itemJobs);
    if (serviceFilter === "abnormal")
      return isStyleMismatchExchangeItem(item, exchangeRules);
    if (serviceFilter === "pending") return label === "待客人選擇";
    if (serviceFilter === "filled") return label === "客人已選，待換貨";
    if (serviceFilter === "expired") return label === "已過期";
    if (serviceFilter === "exchanged") return label === "已換貨";
    if (serviceFilter === "failed") return label === "換貨失敗，請處理";
    if (serviceFilter === "restore_failed")
      return label === "價格還原失敗，請檢查";
    if (serviceFilter === "refund")
      return ["不換貨申請退款", "賣家斷貨"].includes(label);
    if (serviceFilter === "handled") return ["已處理", "已執行斷貨退紅利"].includes(label);
    return false;
  };
  const serviceTaskMatchesFilter = (task) => {
    if (task.taskType === "exchange") return false;
    const label = serviceTaskLabelOf(task);
    if (serviceFilter === "all") return true;
    if (serviceFilter === "pending") return label === "待客人填寫";
    if (serviceFilter === "filled") return label === "客人已送出";
    if (serviceFilter === "expired") return label === "已過期";
    if (serviceFilter === "abnormal") return label === "已過期";
    if (serviceFilter === "handled") return label === "已處理";
    return false;
  };
  useEffect(() => {
    if (["filled", "refund", "exchanged", "handled"].includes(serviceFilter)) {
      setProcessingSortMode("selectionDesc");
      return;
    }
    if (serviceFilter === "all") {
      setProcessingSortMode("paDesc");
    }
  }, [serviceFilter]);
  const processingSortOptions = [
    { value: "paDesc", label: "PA 單號新到舊" },
    { value: "paAsc", label: "PA 單號舊到新" },
    { value: "selectionDesc", label: "送出時間新到舊" },
    { value: "selectionAsc", label: "送出時間舊到新" },
    { value: "createdDesc", label: "建立時間新到舊" },
    { value: "createdAsc", label: "建立時間舊到新" },
    { value: "deadlineAsc", label: "截止時間近到遠" },
    { value: "deadlineDesc", label: "截止時間遠到近" },
    { value: "status", label: "資料狀態分組" },
  ];
  const compareExchangeItems = createProcessingComparator(processingSortMode, {
    pa: paSortValueOf,
    created: createdTimeMsOf,
    selection: selectionTimeMsOf,
    deadline: deadlineTimeMsOf,
    status: (item) => statusLabelOf(item, [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ]),
  });
  const filteredExchangeItems = exchangeItems.filter((item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    const keyword = queueKeyword.trim().toLowerCase();
    return (
      statusMatchesFilter(item, itemJobs) &&
      (!keyword || searchableTextOf(item).includes(keyword))
    );
  }).sort(compareExchangeItems);
  const filteredServiceTasks = serviceTasks
    .filter((task) => {
      const keyword = queueKeyword.trim().toLowerCase();
      const searchable = [
        task.paNumber,
        task.fromName,
        task.recipientPhone,
        task.product?.partNumber,
        task.product?.itemNumber,
        task.product?.name,
        task.originalStyle,
        task.formTitle,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return serviceTaskMatchesFilter(task) && (!keyword || searchable.includes(keyword));
    })
    .sort(createProcessingComparator(processingSortMode, {
      pa: paSortValueOf,
      created: createdTimeMsOf,
      selection: (task) => timeMsOf(task.submittedAt),
      deadline: (task) => timeMsOf(task.serviceDeadline || task.exchangeDeadline),
      status: serviceTaskLabelOf,
    }));
  const visibleServiceTaskIds = filteredServiceTasks.map((task) => task.id);
  const selectedVisibleServiceTaskIds = selectedServiceTaskIds.filter((id) =>
    visibleServiceTaskIds.includes(id),
  );
  const selectedServiceTasks = serviceTasks.filter((task) =>
    selectedServiceTaskIds.includes(task.id),
  );
  const selectedServiceHandledCount = selectedServiceTasks.filter((task) =>
    serviceTaskLabelOf(task) === "已處理",
  ).length;
  const allSelectedServiceTasksHandled =
    selectedServiceTasks.length > 0 &&
    selectedServiceHandledCount === selectedServiceTasks.length;
  const visibleExchangeIds = filteredExchangeItems.map((item) => item.id);
  const selectedVisibleExchangeIds = selectedExchangeIds.filter((id) =>
    visibleExchangeIds.includes(id),
  );
  const exchangeResultSummary = useMemo(() => {
    const byTarget = new Map();
    let exchangeCount = 0;
    let cancelCount = 0;
    let selectedCount = 0;
    filteredExchangeItems.forEach((item) => {
      const selection = item.customerSelection || {};
      if (!selection.action) return;
      const quantity = selectionQuantityOf(item);
      selectedCount += quantity;
      if (selection.action === "cancel") {
        cancelCount += quantity;
        return;
      }
      const targetId = String(
        selection.jamboCommodityId ||
          selection.commodityId ||
          selection.productId ||
          "",
      );
      const targetProduct = selectedTargetProductOf(item) || {};
      const style = selection.style || "無樣式";
      const key = [
        targetId,
        selection.itemNumber || targetProduct.itemNumber || "",
        selection.productName || targetProduct.name || "",
      ].join("::");
      const current = byTarget.get(key) || {
        key,
        targetId,
        productName: selection.productName || targetProduct.name || "未命名商品",
        itemNumber: selection.itemNumber || targetProduct.itemNumber || "",
        image:
          selection.image ||
          selection.photo ||
          selection.productImage ||
          targetProduct.image ||
          targetProduct.photo ||
          "",
        quantity: 0,
        paNumbers: new Set(),
        styles: new Map(),
      };
      current.quantity += quantity;
      if (item.paNumber) current.paNumbers.add(item.paNumber);
      current.styles.set(style, (current.styles.get(style) || 0) + quantity);
      byTarget.set(key, current);
      exchangeCount += quantity;
    });
    const rows = [...byTarget.values()]
      .map((row) => ({
        ...row,
        paCount: row.paNumbers.size,
        styleRows: [...row.styles.entries()]
          .map(([style, quantity]) => ({ style, quantity }))
          .sort((a, b) => String(a.style).localeCompare(String(b.style), "zh-Hant")),
      }))
      .sort((a, b) => {
        if (b.quantity !== a.quantity) return b.quantity - a.quantity;
        return `${a.itemNumber}${a.productName}`.localeCompare(
          `${b.itemNumber}${b.productName}`,
          "zh-Hant",
        );
      });
    return {
      rows,
      exchangeCount,
      cancelCount,
      selectedCount,
    };
  }, [filteredExchangeItems]);
  const pendingSourceSummary = useMemo(() => {
    const bySource = new Map();
    filteredExchangeItems.forEach((item) => {
      const source = item.originalProduct || {};
      const sourceId = String(
        item.jamboCommodityId ||
          source.jamboCommodityId ||
          source.commodityId ||
          source.id ||
          source.itemNumber ||
          source.name ||
          "",
      );
      if (!sourceId) return;
      const style = item.originalStyle || "無樣式";
      const quantity = selectionQuantityOf(item);
      const key = [
        sourceId,
        source.itemNumber || "",
        source.name || "",
      ].join("::");
      const current = bySource.get(key) || {
        key,
        sourceId,
        productName: source.name || "未命名商品",
        itemNumber: source.itemNumber || "",
        image: source.image || "",
        quantity: 0,
        paNumbers: new Set(),
        styles: new Map(),
      };
      current.quantity += quantity;
      if (item.paNumber) current.paNumbers.add(item.paNumber);
      current.styles.set(style, (current.styles.get(style) || 0) + quantity);
      bySource.set(key, current);
    });
    const rows = [...bySource.values()]
      .map((row) => ({
        ...row,
        paCount: row.paNumbers.size,
        styleRows: [...row.styles.entries()]
          .map(([style, quantity]) => ({ style, quantity }))
          .sort((a, b) => String(a.style).localeCompare(String(b.style), "zh-Hant")),
      }))
      .sort((a, b) => {
        if (b.quantity !== a.quantity) return b.quantity - a.quantity;
        return `${a.itemNumber}${a.productName}`.localeCompare(
          `${b.itemNumber}${b.productName}`,
          "zh-Hant",
        );
      });
    return {
      rows,
      totalQuantity: rows.reduce((sum, row) => sum + row.quantity, 0),
      productCount: rows.length,
      paCount: new Set(filteredExchangeItems.map((item) => item.paNumber).filter(Boolean)).size,
    };
  }, [filteredExchangeItems]);
  const serviceFormSummary = useMemo(() => {
    const templateById = new Map(
      serviceTemplates.map((template) => [template.id, template]),
    );
    const byForm = new Map();
    const paNumbers = new Set();
    const totals = {
      totalTasks: 0,
      pendingCount: 0,
      submittedCount: 0,
      handledCount: 0,
      needsHelpCount: 0,
      answeredTasks: 0,
      answeredFields: 0,
      totalFields: 0,
    };

    filteredServiceTasks.forEach((task) => {
      const template =
        templateById.get(task.questionTemplateId || task.formId) || {};
      const formTitle =
        task.formTitle ||
        template.title ||
        template.formTitle ||
        "售後服務表單";
      const productName =
        task.product?.name || task.originalProduct?.name || "未命名商品";
      const label = serviceTaskLabelOf(task);
      const questionRows = serviceQuestionRowsOf(task, template);
      const answeredCount = questionRows.filter((row) => row.filled).length;
      const paNumber = task.paNumber || task.billId || "";
      const formKey = [
        task.questionTemplateId || task.formId || formTitle,
        formTitle,
      ].join("::");
      const current = byForm.get(formKey) || {
        key: formKey,
        formTitle,
        taskCount: 0,
        pendingCount: 0,
        submittedCount: 0,
        handledCount: 0,
        needsHelpCount: 0,
        answeredTasks: 0,
        answeredFields: 0,
        totalFields: 0,
        paNumbers: new Set(),
        productNames: new Set(),
      };

      totals.totalTasks += 1;
      current.taskCount += 1;
      if (paNumber) {
        paNumbers.add(paNumber);
        current.paNumbers.add(paNumber);
      }
      if (productName) current.productNames.add(productName);
      totals.answeredFields += answeredCount;
      totals.totalFields += questionRows.length;
      current.answeredFields += answeredCount;
      current.totalFields += questionRows.length;
      if (answeredCount > 0 || task.submittedAt) {
        totals.answeredTasks += 1;
        current.answeredTasks += 1;
      }

      if (label === "待客人填寫") {
        totals.pendingCount += 1;
        current.pendingCount += 1;
      } else if (label === "客人已送出") {
        totals.submittedCount += 1;
        current.submittedCount += 1;
      } else if (label === "已處理") {
        totals.handledCount += 1;
        current.handledCount += 1;
      } else {
        totals.needsHelpCount += 1;
        current.needsHelpCount += 1;
      }

      byForm.set(formKey, current);
    });

    const rows = [...byForm.values()]
      .map((row) => {
        const productNames = [...row.productNames];
        return {
          ...row,
          paCount: row.paNumbers.size,
          productText:
            productNames.length > 2
              ? `${productNames[0]}等 ${productNames.length} 款商品`
              : productNames.join("、") || "-",
        };
      })
      .sort((a, b) => {
        if (b.taskCount !== a.taskCount) return b.taskCount - a.taskCount;
        return a.formTitle.localeCompare(b.formTitle, "zh-Hant");
      });

    return {
      ...totals,
      rows,
      paCount: paNumbers.size,
    };
  }, [filteredServiceTasks, serviceTemplates]);
  const toggleExchangeSelection = (id) => {
    setSelectedExchangeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };
  const selectVisibleExchangeItems = () => {
    setSelectedExchangeIds((prev) => mergeUnique([...prev, ...visibleExchangeIds]));
  };
  const clearVisibleExchangeSelection = () => {
    setSelectedExchangeIds((prev) =>
      prev.filter((id) => !visibleExchangeIds.includes(id)),
    );
  };
  const selectedPaCount = new Set(
    exchangeItems
      .filter((item) => selectedExchangeIds.includes(item.id) && item.paNumber)
      .map((item) => item.paNumber),
  ).size;
  const selectedExchangeItems = exchangeItems.filter((item) =>
    selectedExchangeIds.includes(item.id),
  );
  const selectedHandledCount = selectedExchangeItems.filter((item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    return ["已處理", "已執行斷貨退紅利"].includes(statusLabelOf(item, itemJobs));
  }).length;
  const selectedSellerOutOfStockItems = selectedExchangeItems.filter((item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    const label = statusLabelOf(item, itemJobs);
    return !item.customerSelection?.action && ["待客人選擇", "已過期"].includes(label);
  });
  const allSelectedExchangeItemsHandled =
    selectedExchangeItems.length > 0 &&
    selectedHandledCount === selectedExchangeItems.length;
  const selectedStockoutRefundItems = selectedExchangeItems.filter((item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    const label = statusLabelOf(item, itemJobs);
    return ["不換貨申請退款", "賣家斷貨"].includes(label) &&
      !item.sellerOutOfStockExecutedAt &&
      item.sellerOutOfStockRefundStatus !== "success";
  });
  const selectedStyleMismatchCount = exchangeItems.filter(
    (item) =>
      selectedExchangeIds.includes(item.id) &&
      isStyleMismatchExchangeItem(item, exchangeRules),
  ).length;
  const exchangeDisplayGroups = filteredExchangeItems.reduce((groups, item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    const displayStatus = statusLabelOf(item, itemJobs);
    const displayPaNumber = formatPaNumber(
      item.paNumber || item.jamboOrderId || item.orderId || "",
    );
    const groupKey = `${displayPaNumber}__${displayStatus}`;
    let group = groups.find((entry) => entry.key === groupKey);
    if (!group) {
      group = {
        key: groupKey,
        paNumber: displayPaNumber,
        status: displayStatus,
        statusTone: statusToneOf(displayStatus),
        fromName: item.fromName || "",
        recipientPhone: item.recipientPhone || "",
        fromPicture: item.fromPicture || "",
        fromId: item.fromId || "",
        items: [],
      };
      groups.push(group);
    }
    group.items.push(item);
    return groups;
  }, []);
  const serviceDisplayGroups = filteredServiceTasks.reduce((groups, task) => {
    const displayStatus = serviceTaskLabelOf(task);
    const displayPaNumber = formatPaNumber(
      task.paNumber || task.billId || task.orderId || "",
    );
    const groupKey = `${displayPaNumber}__${displayStatus}__service`;
    let group = groups.find((entry) => entry.key === groupKey);
    if (!group) {
      group = {
        key: groupKey,
        paNumber: displayPaNumber,
        status: displayStatus,
        statusTone: serviceTaskToneOf(displayStatus),
        fromName: task.fromName || "",
        recipientPhone: task.recipientPhone || task.phone || "",
        fromPicture: task.fromPicture || "",
        fromId: task.fromId || "",
        items: [],
      };
      groups.push(group);
    }
    group.items.push(task);
    return groups;
  }, []);

  const displayGroups = mergeProcessingGroups(
    exchangeDisplayGroups, serviceDisplayGroups, processingSortMode,
    (group, kind) => {
      const item = group.items[0];
      return {
        pa: paSortValueOf(item),
        created: createdTimeMsOf(item),
        selection: kind === "exchange" ? selectionTimeMsOf(item) : timeMsOf(item.submittedAt),
        deadline: kind === "exchange" ? deadlineTimeMsOf(item) : timeMsOf(item.serviceDeadline || item.exchangeDeadline),
        status: group.status,
        id: item.id,
      };
    },
  );

  const toggleExchangeGroupSelection = (ids = []) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) return;
    setSelectedExchangeIds((prev) => {
      const allSelected = validIds.every((id) => prev.includes(id));
      if (allSelected) return prev.filter((id) => !validIds.includes(id));
      return mergeUnique([...prev, ...validIds]);
    });
  };

  const toggleServiceGroupSelection = (ids = []) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) return;
    setSelectedServiceTaskIds((prev) => {
      const allSelected = validIds.every((id) => prev.includes(id));
      if (allSelected) return prev.filter((id) => !validIds.includes(id));
      return mergeUnique([...prev, ...validIds]);
    });
  };

  const toggleServiceTaskSelection = (id) => {
    setSelectedServiceTaskIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const selectVisibleServiceTasks = () => {
    setSelectedServiceTaskIds((prev) => mergeUnique([...prev, ...visibleServiceTaskIds]));
  };

  const clearVisibleServiceTasks = () => {
    setSelectedServiceTaskIds((prev) =>
      prev.filter((id) => !visibleServiceTaskIds.includes(id)),
    );
  };

  const updateServiceTaskStatus = async (taskIds, action) => {
    const ids = Array.isArray(taskIds) ? taskIds.filter(Boolean) : [];
    if (ids.length === 0) {
      alert("請先勾選售後表單任務");
      return;
    }
    if (action === "return_for_refill" && !window.confirm("確定退回選取的已填寫表單，讓客人重新填寫嗎？原答案會保留在退回紀錄中。此操作不會自動發送簡訊；連結或表單期限過期時，請先更新期限並重新發送連結。")) return;
    setIsLoading(true);
    try {
      const updateStatus = adminCallable("tianlanUpdateServiceTaskStatusBatch");
      const result = await updateStatus(withShopScope({ taskIds: ids, action }, currentShop));
      await loadData();
      setSelectedServiceTaskIds((prev) => prev.filter((id) => !ids.includes(id)));
      if (action === "handled") {
        setServiceFilter("handled");
        setProcessingSortMode("selectionDesc");
      } else if (action === "reopen" || action === "return_for_refill") {
        setServiceFilter("all");
        setProcessingSortMode("paDesc");
      }
      alert(action === "return_for_refill" ? `已退回 ${result.data?.updated || 0} 份表單，客人可重新填寫；原答案已保留。${result.data?.skipped ? `另有 ${result.data.skipped} 份不適用，未變更。` : ""}` : action === "handled" ? "已標記已處理，已切換到已處理分類" : action === "reopen" ? "已取消標記已處理" : "已更新售後表單任務狀態");
    } catch (error) {
      alert(`更新售後表單任務失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSelectedServiceTaskStatus = (action) => updateServiceTaskStatus(selectedServiceTaskIds, action);

  const setSelectedServiceTaskDeadline = async (clearDeadline = false) => {
    if (selectedServiceTaskIds.length === 0 || (!clearDeadline && !deadlineDialog.deadline)) return;
    setIsLoading(true);
    try {
      const setDeadline = adminCallable("tianlanSetServiceDeadlineBatch");
      await setDeadline(
        withShopScope(
          {
            taskIds: selectedServiceTaskIds,
            serviceDeadline: clearDeadline ? null : new Date(`${deadlineDialog.deadline}:00+08:00`).toISOString(),
            clearDeadline,
            deadlineSource: "admin",
          },
          currentShop,
        ),
      );
      await loadData();
      setDeadlineDialog((prev) => ({ ...prev, open: false }));
      alert(clearDeadline ? "已解除售後表單期限" : "已設定售後表單期限");
    } catch (error) {
      alert(`設定售後表單期限失敗：${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderServiceTaskCard = (task) => {
    const label = serviceTaskLabelOf(task);
    const tone = serviceTaskToneOf(label);
    const template = serviceTemplates.find((item) => item.id === (task.questionTemplateId || task.formId)) || {};
    const responses = task.responses || {};
    const questions = Array.isArray(template.questions) ? template.questions : [];
    const formTitle = task.formTitle || template.title || template.formTitle || "售後服務表單";
    const answerRows = serviceQuestionRowsOf(task, template);
    const answeredCount = answerRows.filter((row) => row.filled).length;
    const hasResponses = answeredCount > 0 || Boolean(task.submittedAt);
    return (
      <div key={task.id} className={`tl-exchange-line-card tl-service-task-line-card tl-service-card-${tone}`}>
        <div className="tl-exchange-compact-row">
          <label className="tl-service-check">
            <input
              type="checkbox"
              checked={selectedServiceTaskIds.includes(task.id)}
              onChange={() => toggleServiceTaskSelection(task.id)}
            />
          </label>
          <div className="tl-compact-product tl-compact-source-product">
            {task.product?.image || task.originalProduct?.image ? (
              <img
                src={task.product?.image || task.originalProduct?.image}
                alt={task.product?.name || task.originalProduct?.name || "售後商品"}
                className="tl-compact-product-image"
              />
            ) : (
              <div className="tl-compact-product-image tl-admin-product-placeholder">無圖</div>
            )}
            <div className="tl-compact-product-copy">
              <span className="tl-compact-kicker">售後表單任務</span>
              {task.formProductMode === "children" && task.combinedParentProduct && (
                <span className="tl-service-combined-parent">
                  組合商品｜{task.combinedParentProduct.partNumber || task.combinedParentProduct.name || task.combinedParentProduct.commodityId}
                </span>
              )}
              <strong>{task.product?.partNumber || task.product?.itemNumber || task.originalProduct?.itemNumber || "-"}</strong>
              <span>{task.product?.name || task.originalProduct?.name || task.formTitle || "售後服務表單"}</span>
              <div className="tl-compact-chip-row">
                <em>{task.originalStyle || "無樣式"}</em>
                <em>{task.quantity || 1} 件</em>
                <em>{formTitle}</em>
              </div>
            </div>
          </div>
          <div className="tl-compact-status-stack">
            <span className={`tl-service-status tl-service-status-${tone}`}>{label}</span>
            <dl>
              <div>
                <dt>表單</dt>
                <dd>{formTitle}</dd>
              </div>
              <div>
                <dt>截止</dt>
                <dd>{formatDeadline(task.serviceDeadline || task.exchangeDeadline) || "未設定"}</dd>
              </div>
              <div>
                <dt>送出</dt>
                <dd>{formatDeadline(task.submittedAt) || "尚未送出"}</dd>
              </div>
              <div>
                <dt>簡訊</dt>
                <dd>{smsStatusLabelOf(task.formSmsStatus)}</dd>
              </div>
            </dl>
          </div>
          <div className="tl-compact-actions">
            {hasResponses && (
              <button className="modern-btn modern-btn-secondary" onClick={() => updateServiceTaskStatus([task.id], "return_for_refill")} disabled={isLoading}>
                退回重填
              </button>
            )}
            <button
              className={label === "已處理" ? "modern-btn modern-btn-secondary" : "modern-btn modern-btn-primary"}
              onClick={() => updateServiceTaskStatus([task.id], label === "已處理" ? "reopen" : "handled")}
              disabled={isLoading}
            >
              {label === "已處理" ? "取消標記已處理" : "標記已處理"}
            </button>
            <button className="modern-btn modern-btn-danger" onClick={() => removeServiceTask(task)} disabled={isLoading}>
              移除
            </button>
          </div>
        </div>
        {(questions.length > 0 || Object.keys(responses).length > 0) && (
          <div className={`tl-service-answer-panel ${hasResponses ? "has-response" : "is-empty"}`}>
            <div className="tl-service-answer-head">
              <div>
                <strong>客人回覆</strong>
                <span>
                  {hasResponses
                    ? `${answeredCount} / ${answerRows.length || questions.length} 項已填`
                    : `${answerRows.length || questions.length} 個填寫項目`}
                </span>
              </div>
              <div className="tl-service-answer-meta">
                <span>送出：{formatDeadline(task.submittedAt) || "尚未送出"}</span>
                <span>狀態：{label}</span>
              </div>
            </div>
            <div className="tl-service-answer-grid">
              {answerRows.map((row, index) => (
                <div
                  className={`tl-service-answer-item ${row.filled ? "" : "is-empty"}`}
                  key={row.id || index}
                >
                  <span className="tl-service-answer-index">{index + 1}</span>
                  <div>
                    <em>{row.label}</em>
                    <strong>{row.filled ? row.answerText : "尚未填寫"}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {task.lastReturnedSubmission && (
          <details className="tl-service-answer-panel">
            <summary>上次退回紀錄 · {formatDeadline(task.lastReturnedSubmission.returnedAt)}</summary>
            <p>原送出時間：{formatDeadline(task.lastReturnedSubmission.submittedAt) || "未記錄"}</p>
            <div className="tl-service-answer-grid">
              {serviceQuestionRowsOf({ ...task, ...task.lastReturnedSubmission }, template).map((row, index) => (
                <div className="tl-service-answer-item" key={row.id || index}>
                  <span className="tl-service-answer-index">{index + 1}</span>
                  <div><em>{row.label}</em><strong>{row.filled ? row.answerText : "未填寫"}</strong></div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  };

  const renderExchangeItemCard = (item) => {
    const itemJobs = [
      ...(jobsByExchangeId[item.id] || []),
      ...(jobsByExchangeId[item.paNumber] || []),
    ];
    const displayStatus = statusLabelOf(item, itemJobs);
    const statusTone = statusToneOf(displayStatus);
    const jobStatusText =
      itemJobs
        .map((job) => exchangeJobStatusLabelOf(job.status))
        .filter(Boolean)
        .join("、") || "尚未開始";
    const selection = item.customerSelection || {};
    const failureText =
      item.exchangeDifferenceRefundError ||
      item.exchangeCompletionSmsError ||
      item.failureReason ||
      item.jobError ||
      item.jamboExchangeError ||
      itemJobs
        .map((job) => job.error || job.restoreError)
        .filter(Boolean)[0] ||
      "";
    const liveOptions = item.liveExchangeOptions || [];
    const styleMismatch = hasRuleStyleMismatchExchangeItem(item, exchangeRules);
    const noSelectableOptions = hasNoSelectableExchangeOptions(item);
    const sourceStyleCandidates = sourceStyleCandidatesForItem(exchangeRules, item);
    const isHandledStatus = ["已處理", "已執行斷貨退紅利"].includes(displayStatus);
    const canExecuteStockoutRefund =
      ["不換貨申請退款", "賣家斷貨"].includes(displayStatus) &&
      !item.sellerOutOfStockExecutedAt &&
      item.sellerOutOfStockRefundStatus !== "success";
    const canReturnSelection =
      item.customerSelection?.action &&
      !isHandledStatus &&
      displayStatus !== "價格還原失敗，請檢查";
    const canRunExchange =
      selection.action === "exchange" &&
      item.paNumber &&
      !["已換貨", "已處理", "已執行斷貨退紅利"].includes(displayStatus);
    const canMarkHandled = !isHandledStatus && !canExecuteStockoutRefund;
    const canRetryCompletion = (
      isExchangeCompleted(item) && ["failed", "blocked"].includes(item.exchangeDifferenceRefundStatus)
    ) || item.exchangeCompletionSmsStatus === "failed";
    const canEditTaskDifference =
      !isHandledStatus &&
      !isExchangeCompleted(item) &&
      !["sending", "success", "uncertain"].includes(item.exchangeDifferenceRefundStatus) &&
      !itemJobs.some((job) => ["running", "success"].includes(job.status));
    const selectedProduct = selectedTargetProductOf(item) || {};
    const selectedProductImage =
      selection.image ||
      selection.photo ||
      selection.productImage ||
      selectedProduct.image ||
      selectedProduct.photo ||
      "";
    const selectedProductName =
      displayStatus === "賣家斷貨"
        ? "賣家斷貨"
        : selection.action === "cancel"
        ? "不換貨，申請退款"
        : selection.productName || selectedProduct.name || "尚未選擇";
    const selectedItemNumber =
      selection.itemNumber ||
      selectedProduct.itemNumber ||
      selectedProduct.partNumber ||
      "";
    const selectionSubmittedText = formatDeadline(selectionTimeValueOf(item));
    const differenceRewardInput = differenceRewardInputFor(item);
    const showDifferenceRewardInput =
      selection.action === "exchange" &&
      selection.refundPriceDifference === true &&
      item.exchangeDifferenceRefundAmountSet !== true &&
      item.exchangeDifferenceRefundStatus !== "success";

    return (
      <div
        key={item.id}
        className={`tl-exchange-line-card tl-service-card-${statusTone}`}
      >
        <div className="tl-exchange-compact-row">
          <label className="tl-service-check">
            <input
              type="checkbox"
              checked={selectedExchangeIds.includes(item.id)}
              onChange={() => toggleExchangeSelection(item.id)}
            />
          </label>

          <div className="tl-compact-product tl-compact-source-product">
            {item.originalProduct?.image ? (
              <img
                src={item.originalProduct.image}
                alt={item.originalProduct?.name || "缺貨商品"}
                className="tl-compact-product-image"
              />
            ) : (
              <div className="tl-compact-product-image tl-admin-product-placeholder">
                無圖
              </div>
            )}
            <div className="tl-compact-product-copy">
              <span className="tl-compact-kicker">缺貨商品</span>
              <strong>{item.originalProduct?.itemNumber || "缺貨商品"}</strong>
              <span>{item.originalProduct?.name || "-"}</span>
              <div className="tl-compact-chip-row">
                <em>{item.originalStyle || "無樣式"}</em>
                <em>{item.quantity || 1} 件</em>
                <em>${item.amount || 0}</em>
              </div>
            </div>
          </div>

          <div className="tl-compact-flow-arrow" aria-hidden="true">
            →
          </div>

          <div className="tl-compact-product tl-compact-selection-product">
            {selection.action === "exchange" && selectedProductImage ? (
              <img
                src={selectedProductImage}
                alt={selectedProductName || "換貨商品"}
                className="tl-compact-product-image"
              />
            ) : (
              <div
                className={`tl-compact-product-image tl-admin-product-placeholder ${
                  selection.action === "cancel" ? "tl-admin-product-muted" : ""
                }`}
              >
                {selection.action === "cancel" ? "退" : "未"}
              </div>
            )}
            <div className="tl-compact-product-copy">
              <span className="tl-compact-kicker">客戶選擇</span>
              <strong>{selectedItemNumber || "-"}</strong>
              <span>{selectedProductName}</span>
              <div className="tl-compact-chip-row">
                <em>{selection.style || "無樣式"}</em>
                {item.stockReservation && (
                  <em>
                    保留：
                    {item.stockReservation.unlimited
                      ? "無上限"
                      : `${item.stockReservation.quantity || 1} 件`}
                  </em>
                )}
              </div>
            </div>
          </div>

          <div className="tl-compact-status-stack">
            <span className={`tl-service-status tl-service-status-${statusTone}`}>
              {displayStatus}
            </span>
            <dl>
              <div>
                <dt>自動換貨</dt>
                <dd>{jobStatusText}</dd>
              </div>
              <div>
                <dt>明細</dt>
                <dd>{item.jamboOrderItemId || item.orderId || "-"}</dd>
              </div>
              <div>
                <dt>截止</dt>
                <dd>{formatDeadline(item.exchangeDeadline) || "未設定"}</dd>
              </div>
              <div>
                <dt>送出</dt>
                <dd>{selectionSubmittedText || "尚未送出"}</dd>
              </div>
              {selection.action === "exchange" && (
                <>
                  <div>
                    <dt>差額紅利</dt>
                    <dd>{differenceRefundLabelOf(item)}</dd>
                  </div>
                  <div>
                    <dt>完成簡訊</dt>
                    <dd>{completionSmsLabelOf(item)}</dd>
                  </div>
                </>
              )}
              {(item.sellerOutOfStockExecutedAt || item.sellerOutOfStockRefundAt) && (
                <div>
                  <dt>斷貨</dt>
                  <dd>{formatDeadline(item.sellerOutOfStockExecutedAt || item.sellerOutOfStockRefundAt)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="tl-compact-actions">
            {showDifferenceRewardInput && (
              <label className="tl-difference-reward-input">
                <span>退還差額紅利</span>
                <span className="tl-difference-reward-field">
                  <input
                    type="number"
                    min="0"
                    max="1000000"
                    step="1"
                    inputMode="numeric"
                    value={differenceRewardInput}
                    onChange={(event) => setDifferenceRewardInputs((current) => ({
                      ...current,
                      [item.id]: event.target.value,
                    }))}
                    placeholder="0"
                    disabled={isLoading}
                    aria-label="退還差額紅利點數"
                  />
                  <em>點</em>
                </span>
                <small>由小幫手確認；沒有差額請輸入 0</small>
              </label>
            )}
            {(styleMismatch || noSelectableOptions) && (
              <button
                className="modern-btn modern-btn-primary"
                onClick={() => repairExchangeItemStyle(item)}
                disabled={isLoading}
              >
                {styleMismatch ? "修復樣式" : "重抓規則"}
              </button>
            )}
            {canEditTaskDifference && (
              <button
                className="modern-btn modern-btn-secondary"
                onClick={() => openDifferenceTaskEditor(item)}
                disabled={isLoading}
              >
                編輯任務
              </button>
            )}
            {canRunExchange && (
              <button
                className="modern-btn modern-btn-primary"
                onClick={() => enqueueAndRun(item)}
                disabled={isLoading}
              >
                執行換貨
              </button>
            )}
            {canReturnSelection && (
              <button
                className="modern-btn modern-btn-secondary"
                onClick={() => returnExchangeItemToCustomer(item)}
                disabled={isLoading}
              >
                退回重選
              </button>
            )}
            {canMarkHandled && (
              <button
                className="modern-btn modern-btn-secondary"
                onClick={() => markExchangeItemHandled(item)}
                disabled={isLoading}
              >
                標記已處理
              </button>
            )}
            {canRetryCompletion && (
              <button
                className="modern-btn modern-btn-primary"
                onClick={() => retryExchangeCompletion(item)}
                disabled={isLoading}
              >
                重試退點／簡訊
              </button>
            )}
            {canExecuteStockoutRefund && (
              <button
                className="modern-btn modern-btn-danger"
                onClick={() => setStockoutRefundDialog({ open: true, ids: [item.id] })}
                disabled={isLoading}
              >
                執行斷貨退紅利
              </button>
            )}
            {isHandledStatus && (
              <button
                className="modern-btn modern-btn-secondary"
                onClick={() => openCancelHandledDialog([item.id])}
                disabled={isLoading}
              >
                取消標記已處理
              </button>
            )}
            <button
              className="modern-btn modern-btn-secondary"
              onClick={() => removeExchangeItem(item)}
              disabled={isLoading}
            >
              移除
            </button>
          </div>
        </div>

        {(failureText || styleMismatch || noSelectableOptions) && (
          <div className="tl-compact-alerts">
            {failureText && <span>失敗原因：{failureText}</span>}
            {styleMismatch && (
              <span>
                樣式異常：目前記錄「{item.originalStyle || "未記錄"}」
                {sourceStyleCandidates.length > 0
                  ? `，規則樣式：${sourceStyleCandidates.slice(0, 4).join("、")}${sourceStyleCandidates.length > 4 ? "..." : ""}`
                  : ""}
              </span>
            )}
            {noSelectableOptions && (
              <span>可選商品異常：沒有可供客人選擇的換貨商品或可選數量。</span>
            )}
          </div>
        )}

        <details className="tl-compact-rule-details">
          <summary>
            規則可選數量
            <span>
              {liveOptions.length > 0 ? `${liveOptions.length} 個可換商品` : "沒有可換商品"}
            </span>
          </summary>
          {liveOptions.length > 0 ? (
            <div className="tl-live-stock-list">
              {liveOptions.slice(0, 4).map((product) => {
                const styles = styleLabelsOf(product);
                const displayStyles = styles.length > 0 ? styles : [""];
                return (
                  <div key={commodityIdOf(product)} className="tl-live-stock-item">
                    <span>
                      {product.partNumber || product.itemNumber || commodityIdOf(product)}{" "}
                      {product.name || ""}
                    </span>
                    <small>
                      {displayStyles
                        .map(
                          (style) =>
                            `${style || "無樣式"}：${stockTextOf(product, style)}`,
                        )
                        .join(" / ")}
                    </small>
                  </div>
                );
              })}
              {liveOptions.length > 4 && (
                <small>另有 {liveOptions.length - 4} 個可換商品</small>
              )}
            </div>
          ) : (
            <span className="tl-compact-empty-note">
              目前規則沒有可換商品或樣式庫存設定。
            </span>
          )}
        </details>
      </div>
    );
  };

  const exportExchangeProcessingList = (target = "all") => {
    const selectedExchangeIdSet = new Set(selectedExchangeIds);
    const selectedServiceTaskIdSet = new Set(selectedServiceTaskIds);
    const exportExchange = target !== "service";
    const exportService = target !== "exchange";
    const hasSelection =
      (exportExchange && selectedExchangeIds.length > 0) ||
      (exportService && selectedServiceTaskIds.length > 0);
    const sourceExchangeItems =
      !exportExchange
        ? []
        : selectedExchangeIds.length > 0
        ? exchangeItems.filter((item) => selectedExchangeIdSet.has(item.id))
        : hasSelection
          ? []
          : filteredExchangeItems;
    const sourceServiceTasks =
      !exportService
        ? []
        : selectedServiceTaskIds.length > 0
        ? serviceTasks.filter((task) => selectedServiceTaskIdSet.has(task.id))
        : hasSelection
          ? []
          : filteredServiceTasks;
    if (sourceExchangeItems.length + sourceServiceTasks.length === 0) {
      alert("目前沒有可匯出的售後任務資料");
      return;
    }
    const questionHeaderMap = new Map();
    sourceServiceTasks.forEach((task) => {
      const template =
        serviceTemplates.find((item) => item.id === (task.questionTemplateId || task.formId)) || {};
      const questions = Array.isArray(template.questions) ? template.questions : [];
      const responseLabels = task.responseLabels || {};
      questions.forEach((question) => {
        const questionId = String(question.id || "").trim();
        if (!questionId) return;
        const label = String(question.label || responseLabels[questionId] || questionId).trim();
        const header = `表單回覆：${label || questionId}`;
        if (!questionHeaderMap.has(questionId)) {
          questionHeaderMap.set(questionId, { id: questionId, header, question });
        }
      });
      Object.keys(task.responses || {}).forEach((questionId) => {
        if (questionHeaderMap.has(questionId)) return;
        const label = String(responseLabels[questionId] || questionId).trim();
        questionHeaderMap.set(questionId, {
          id: questionId,
          header: `表單回覆：${label || questionId}`,
        });
      });
    });
    const questionHeaders = [...questionHeaderMap.values()];
    const headers = [
      "付款單",
      "客人姓名",
      "電話",
      "任務類型",
      "表單名稱",
      "來源組合商品",
      "缺貨料號",
      "缺貨商品",
      "缺貨樣式",
      "客人選擇",
      "換貨料號",
      "換貨商品",
      "換貨樣式",
      "資料狀態",
      "自動換貨狀態",
      "差額紅利",
      "換貨完成簡訊",
      "截止時間",
      "斷貨時間",
      "訂單明細 ID",
      "錯誤原因",
      "建立時間",
      "更新／送出時間",
      ...questionHeaders.map((item) => item.header),
    ];
    const emptyQuestionAnswers = questionHeaders.map(() => "");
    const exchangeRows = sourceExchangeItems.map((item) => {
      const itemJobs = [
        ...(jobsByExchangeId[item.id] || []),
        ...(jobsByExchangeId[item.paNumber] || []),
      ];
      const selection = item.customerSelection || {};
      const failureText =
        item.exchangeDifferenceRefundError ||
        item.exchangeCompletionSmsError ||
        item.failureReason ||
        item.jobError ||
        item.jamboExchangeError ||
        itemJobs
          .map((job) => job.error || job.restoreError)
          .filter(Boolean)[0] ||
        "";
      const jobStatusText =
        itemJobs
          .map((job) => exchangeJobStatusLabelOf(job.status))
          .filter(Boolean)
          .join("、") || "尚未開始";
      const statusText = statusLabelOf(item, itemJobs);
      return [
        formatPaNumber(item.paNumber || item.jamboOrderId || item.orderId || ""),
        item.fromName || "",
        String(item.recipientPhone || item.phone || ""),
        "換貨任務",
        "",
        "",
        item.originalProduct?.itemNumber || "",
        item.originalProduct?.name || "",
        item.originalStyle || "",
        statusText === "賣家斷貨"
          ? "賣家斷貨"
          : selection.action === "cancel"
          ? "不換貨，申請退款"
          : selection.action === "exchange"
            ? "換貨"
            : "尚未選擇",
        selection.itemNumber || "",
        selection.productName || "",
        selection.style || "",
        statusText,
        jobStatusText,
        differenceRefundLabelOf(item),
        completionSmsLabelOf(item),
        formatExcelDateTime(item.exchangeDeadline),
        formatExcelDateTime(item.sellerOutOfStockExecutedAt || item.sellerOutOfStockRefundAt),
        item.jamboOrderItemId || item.orderId || "",
        failureText,
        formatExcelDateTime(item.createdAt || item.createdTime),
        formatExcelDateTime(
          item.selectedAt ||
            item.updatedAt ||
            item.customerSelection?.selectedAt ||
            item.deadlineSetAt,
        ),
        ...emptyQuestionAnswers,
      ];
    });
    const formatServiceAnswerForExcel = (value, question) => formatServiceAnswerForDisplay(value, question);
    const serviceRows = sourceServiceTasks.map((task) => {
      const template =
        serviceTemplates.find((item) => item.id === (task.questionTemplateId || task.formId)) || {};
      const formTitle = task.formTitle || template.title || template.formTitle || "售後服務表單";
      const statusText = serviceTaskLabelOf(task);
      const product = task.product || task.originalProduct || {};
      const failureText =
        task.failureReason ||
        task.errorReason ||
        task.error ||
        "";
      return [
        formatPaNumber(task.paNumber || task.billId || task.orderId || ""),
        task.fromName || task.customerName || "",
        String(task.recipientPhone || task.phone || ""),
        "表單任務",
        formTitle,
        task.formProductMode === "children"
          ? [task.combinedParentProduct?.partNumber || task.combinedParentProduct?.itemNumber, task.combinedParentProduct?.name]
            .filter(Boolean)
            .join(" ")
          : "",
        product.itemNumber || product.partNumber || "",
        product.name || task.productName || "",
        task.originalStyle || task.style || "",
        "表單回覆",
        "",
        "",
        "",
        statusText,
        "不適用",
        "不適用",
        "不適用",
        formatExcelDateTime(task.serviceDeadline || task.exchangeDeadline),
        "",
        task.jamboOrderItemId || task.orderItemId || task.orderId || "",
        failureText,
        formatExcelDateTime(task.createdAt || task.createdTime),
        formatExcelDateTime(task.submittedAt || task.updatedAt || task.deadlineSetAt),
        ...questionHeaders.map((question) => formatServiceAnswerForExcel((task.responses || {})[question.id], question.question)),
      ];
    });
    const body = [...exchangeRows, ...serviceRows];
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
      { wch: 24 },
      { wch: 30 },
      { wch: 16 },
      { wch: 28 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 28 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 36 },
      { wch: 20 },
      { wch: 20 },
      ...questionHeaders.map(() => ({ wch: 28 })),
    ];
    for (let row = 2; row <= body.length + 1; row += 1) {
      const cell = worksheet[`C${row}`];
      if (cell) {
        cell.t = "s";
        cell.z = "@";
      }
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "售後任務處理清單");
    const dateText = new Date().toISOString().slice(0, 10);
    const scopeText =
      hasSelection ? `已選${body.length}筆` : "目前篩選";
    XLSX.writeFile(workbook, `直播售後任務處理清單_${scopeText}_${dateText}.xlsx`);
  };

  const handlePickCommodity = (commodity) => {
    const nextTaskType = taskTypeOfCommodity(commodity);
    const commodityId = String(commodity?.commodityId || commodity?.id || "");
    if (importMode === "all") {
      setSelectedCommodities((prev) => {
        const exists = prev.some((item) => String(item.commodityId || item.id || "") === commodityId);
        if (exists) {
          const next = prev.filter((item) => String(item.commodityId || item.id || "") !== commodityId);
          setSelectedCommodity(next[0] || null);
          if (next[0]) setTaskType(taskTypeOfCommodity(next[0]));
          return next;
        }
        if (prev.length > 0 && taskTypeOfCommodity(prev[0]) !== nextTaskType) {
          alert("多選商品時請選擇相同任務類型；換貨任務與表單任務請分開建立。");
          return prev;
        }
        const next = [...prev, commodity];
        setSelectedCommodity(next[0] || commodity);
        setTaskType(nextTaskType);
        return next;
      });
    } else {
      setSelectedCommodity(commodity);
      setSelectedCommodities(commodity ? [commodity] : []);
      setTaskType(nextTaskType);
    }
    setSelectedStyles([]);
    setOrders([]);
    setSelectedOrders([]);
    setSearchSummary(null);
    setExcelPreview(null);
    setSelectedExcelRowKeys(new Set());
  };

  const toggleOrderSelection = (order) => {
    if (!isOrderSelectable(order)) return;
    const orderItemId = orderItemIdOf(order);
    if (!orderItemId) return;
    setSelectedOrders((prev) => {
      if (prev.some((item) => orderItemIdOf(item) === orderItemId)) {
        return prev.filter((item) => orderItemIdOf(item) !== orderItemId);
      }
      return [...prev, { ...order, takeQty: availableImportQtyOf(order) || 1 }];
    });
  };

  const selectVisibleOrders = () => {
    const selectedById = new Map(
      selectedOrders.map((order) => [orderItemIdOf(order), order]),
    );
    orders.forEach((order) => {
      const orderItemId = orderItemIdOf(order);
      if (orderItemId && isOrderSelectable(order) && !selectedById.has(orderItemId)) {
        selectedById.set(orderItemId, {
          ...order,
          takeQty: availableImportQtyOf(order) || 1,
        });
      }
    });
    setSelectedOrders(Array.from(selectedById.values()));
  };

  const toggleStyle = (style) => {
    setSelectedStyles((prev) =>
      prev.includes(style)
        ? prev.filter((item) => item !== style)
        : [...prev, style],
    );
  };
  const excelPreviewProgressPercent =
    excelPreviewProgress?.searchableRows > 0
      ? Math.min(
          100,
          Math.round(
            ((excelPreviewProgress.completedRows || 0) /
              excelPreviewProgress.searchableRows) *
              100,
          ),
        )
      : excelPreviewProgress?.totalRows > 0
        ? 100
        : 0;

  const renderExchangeBatchBar = (position = "top") => (
    <div
      className={`tl-batch-bar ${position === "bottom" ? "tl-batch-bar-bottom" : ""}`}
    >
      <span>
        目前結果 {visibleExchangeIds.length} 筆，已選{" "}
        {selectedExchangeIds.length} 筆
      </span>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={selectVisibleExchangeItems}
        disabled={isLoading || visibleExchangeIds.length === 0}
      >
        全選目前結果
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={clearVisibleExchangeSelection}
        disabled={isLoading || selectedVisibleExchangeIds.length === 0}
      >
        取消目前勾選
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={exportExchangeProcessingList}
        disabled={
          isLoading ||
          (selectedExchangeIds.length === 0 && filteredExchangeItems.length === 0)
        }
      >
        匯出 Excel
        {selectedExchangeIds.length > 0
          ? ` ${selectedExchangeIds.length} 筆`
          : " 目前篩選"}
      </button>
      <button
        className="modern-btn modern-btn-primary"
        onClick={() => openDeadlineDialog("exchange")}
        disabled={isLoading || selectedExchangeIds.length === 0}
      >
        設定換貨期限
      </button>
      <button
        className="modern-btn modern-btn-primary"
        onClick={() =>
          setSmsDialog((prev) => ({
            ...prev,
            open: true,
            mode: "immediate",
            smsKind: serviceFilter === "refund" ? "stockout" : "exchange",
            messages: [],
            copied: false,
            sentResults: [],
            quickConfirm: null,
          }))
        }
        disabled={isLoading}
      >
        {serviceFilter === "refund" ? "發送斷貨簡訊" : "發送換貨簡訊"}
        {selectedPaCount ? ` ${selectedPaCount} 張 PA` : ""}
      </button>
      {serviceFilter === "refund" && (
        <button
          className="modern-btn modern-btn-danger"
          onClick={openStockoutRefundDialog}
          disabled={isLoading || selectedStockoutRefundItems.length === 0}
        >
          執行斷貨退紅利
          {selectedStockoutRefundItems.length
            ? ` ${selectedStockoutRefundItems.length} 筆`
            : ""}
        </button>
      )}
      <button
        className="modern-btn modern-btn-primary"
        onClick={runSelectedExchanges}
        disabled={isLoading || selectedExchangeIds.length === 0}
      >
        執行勾選換貨
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={repairSelectedExchangeItemStyles}
        disabled={isLoading || selectedStyleMismatchCount === 0}
      >
        修復勾選異常{selectedStyleMismatchCount ? ` ${selectedStyleMismatchCount} 筆` : ""}
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={batchMarkSellerOutOfStock}
        disabled={isLoading || selectedSellerOutOfStockItems.length === 0}
      >
        批次賣家斷貨
        {selectedSellerOutOfStockItems.length
          ? ` ${selectedSellerOutOfStockItems.length} 筆`
          : ""}
      </button>
      {allSelectedExchangeItemsHandled ? (
        <button
          className="modern-btn modern-btn-secondary"
          onClick={() => openCancelHandledDialog(selectedExchangeIds)}
          disabled={isLoading || selectedExchangeIds.length === 0}
        >
          取消標記已處理
        </button>
      ) : (
        <>
          <button
            className="modern-btn modern-btn-primary"
            onClick={() => batchUpdateServiceStatus("handled")}
            disabled={isLoading || selectedExchangeIds.length === 0}
          >
            標記已處理
          </button>
          <button
            className="modern-btn modern-btn-secondary"
            onClick={() => batchUpdateServiceStatus("pending")}
            disabled={isLoading || selectedExchangeIds.length === 0}
          >
            批次退回重選
          </button>
        </>
      )}
      <button
        className="modern-btn modern-btn-danger"
        onClick={removeSelectedExchangeItems}
        disabled={isLoading || selectedExchangeIds.length === 0}
      >
        移除選擇
      </button>
    </div>
  );

  const renderServiceTaskBatchBar = () => (
    <div className="tl-batch-bar tl-service-form-batch-bar">
      <span>
        表單任務 {visibleServiceTaskIds.length} 筆，已選 {selectedServiceTaskIds.length} 筆
      </span>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={selectVisibleServiceTasks}
        disabled={isLoading || visibleServiceTaskIds.length === 0}
      >
        全選表單任務
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={clearVisibleServiceTasks}
        disabled={isLoading || selectedVisibleServiceTaskIds.length === 0}
      >
        取消表單勾選
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={() => exportExchangeProcessingList("service")}
        disabled={
          isLoading ||
          (selectedServiceTaskIds.length === 0 && filteredServiceTasks.length === 0)
        }
      >
        匯出 Excel
        {selectedServiceTaskIds.length > 0
          ? ` ${selectedServiceTaskIds.length} 筆`
          : " 目前篩選"}
      </button>
      <button
        className="modern-btn modern-btn-primary"
        onClick={() => openDeadlineDialog("service")}
        disabled={isLoading || selectedServiceTaskIds.length === 0}
      >
        設定表單期限
      </button>
      <button
        className="modern-btn modern-btn-primary"
        onClick={() =>
          setSmsDialog((prev) => ({
            ...prev,
            open: true,
            mode: "immediate",
            smsKind: "form",
            messages: [],
            copied: false,
            quickConfirm: null,
            sentResults: [],
          }))
        }
        disabled={isLoading}
      >
        發送表單簡訊
      </button>
      <button
        className="modern-btn modern-btn-secondary"
        onClick={() => updateSelectedServiceTaskStatus("return_for_refill")}
        disabled={isLoading || selectedServiceTaskIds.length === 0}
      >
        退回重填
      </button>
      {allSelectedServiceTasksHandled ? (
        <button
          className="modern-btn modern-btn-secondary"
          onClick={() => updateSelectedServiceTaskStatus("reopen")}
          disabled={isLoading || selectedServiceTaskIds.length === 0}
        >
          取消標記已處理
        </button>
      ) : (
        <button
          className="modern-btn modern-btn-secondary"
          onClick={() => updateSelectedServiceTaskStatus("handled")}
          disabled={isLoading || selectedServiceTaskIds.length === 0}
        >
          標記已處理
        </button>
      )}
      <button
        className="modern-btn modern-btn-danger"
        onClick={removeSelectedServiceTasks}
        disabled={isLoading || selectedServiceTaskIds.length === 0}
      >
        移除選擇
      </button>
    </div>
  );

  const smsKindLabel =
    smsDialog.smsKind === "stockout"
      ? "斷貨"
      : smsDialog.smsKind === "form"
        ? "表單"
        : "換貨";
  const activeSmsSelectedCount =
    smsDialog.smsKind === "form" ? selectedServiceTaskIds.length : selectedExchangeIds.length;

  const openCustomerLinkDialog = (paNumber) => {
    setCustomerLinkDialog({
      open: true,
      paNumber,
      expiresInHours: 168,
      url: "",
      qrDataUrl: "",
      expiresAt: "",
      loading: false,
      copied: false,
      error: "",
    });
  };

  const createCustomerAccessLink = async () => {
    setCustomerLinkDialog((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const createLink = adminCallable("tianlanCreateCustomerAccessLink");
      const response = await createLink(withShopScope({
        paNumber: customerLinkDialog.paNumber,
        expiresInHours: customerLinkDialog.expiresInHours,
      }, currentShop));
      const url = new URL(response.data?.path || "/tianlan/customer", window.location.origin).toString();
      const qrCodeModule = await import("qrcode");
      const qrDataUrl = await qrCodeModule.default.toDataURL(url, { width: 280, margin: 1, errorCorrectionLevel: "M" });
      setCustomerLinkDialog((previous) => ({
        ...previous,
        loading: false,
        url,
        qrDataUrl,
        expiresAt: response.data?.expiresAt || "",
      }));
    } catch (error) {
      setCustomerLinkDialog((previous) => ({ ...previous, loading: false, error: error.message || "建立失敗" }));
    }
  };

  const copyCustomerAccessLink = async () => {
    if (!customerLinkDialog.url) return;
    await navigator.clipboard.writeText(customerLinkDialog.url);
    setCustomerLinkDialog((previous) => ({ ...previous, copied: true }));
  };

  const switchWorkspace = (view, filter = null) => {
    setWorkspaceView(view);
    if (filter) setServiceFilter(filter);
  };

  return (
    <div className="modern-page tl-order-console">
      <div className="modern-page-header">
        <div className="modern-container">
          <div className="modern-page-title">
            <Link to="/exchange/tianlan" className="modern-back-btn">
              返回首頁
            </Link>
            <div>
              <h1>訂單售後服務後台</h1>
              <div className="modern-page-subtitle">
                目前商店：{currentShop.storeName}（{currentShop.shopUserId}）
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modern-container tl-admin-workspace">
        <section className="tl-admin-hero tl-order-console-hero">
          <div>
            <div className="tl-admin-kicker">ORDER CARE</div>
            <h2>售後工作台</h2>
            <p>
              建立任務、追蹤客人填寫與完成後續處理。表單送出後直接進入待處理，可立即查看客人答案。
            </p>
          </div>
          <div className="tl-order-hero-actions">
            <button type="button" className="modern-btn modern-btn-primary" onClick={() => switchWorkspace("create")}>
              建立新任務
            </button>
            <button type="button" className="modern-btn modern-btn-secondary" onClick={() => switchWorkspace("queue", "filled")}>
              查看已送出 {dashboardCounts.readyToExchange}
            </button>
            <span>{currentShop.storeName}</span>
          </div>
        </section>

        <div className="tl-status-grid tl-order-metric-strip">
          <button type="button" className="tl-status-card" onClick={() => switchWorkspace("queue", "pending")}>
            <strong>{dashboardCounts.waitingCustomer}<small className="tl-metric-unit"> 筆</small></strong>
            <span>待客人處理</span>
            <small className="tl-metric-customer-count" title="同商店、同收件電話合併計算；缺電話時依客人帳號辨識。同一客人可能同時出現在兩種狀態。">{dashboardCounts.customers.waitingCustomer.size} 位客人{dashboardCounts.unidentified.waitingCustomer > 0 ? ` · ${dashboardCounts.unidentified.waitingCustomer} 筆待辨識` : ""}</small>
          </button>
          <button type="button" className="tl-status-card" onClick={() => switchWorkspace("queue", "filled")}>
            <strong>{dashboardCounts.readyToExchange}<small className="tl-metric-unit"> 筆</small></strong>
            <span>客人已送出</span>
            <small className="tl-metric-customer-count" title="同商店、同收件電話合併計算；缺電話時依客人帳號辨識。同一客人可能同時出現在兩種狀態。">{dashboardCounts.customers.readyToExchange.size} 位客人{dashboardCounts.unidentified.readyToExchange > 0 ? ` · ${dashboardCounts.unidentified.readyToExchange} 筆待辨識` : ""}</small>
          </button>
          <button type="button" className="tl-status-card" onClick={() => switchWorkspace("queue", "all")}>
            <strong>{dashboardCounts.running}</strong>
            <span>系統處理中</span>
          </button>
          <button type="button" className="tl-status-card" onClick={() => switchWorkspace("history", "handled")}>
            <strong>{dashboardCounts.completed}</strong>
            <span>已完成</span>
          </button>
          <button type="button" className="tl-status-card is-warning" onClick={() => switchWorkspace("queue", "abnormal")}>
            <strong>{dashboardCounts.needsHelp}</strong>
            <span>需要協助</span>
          </button>
        </div>

        <nav className="tl-order-workspaces" aria-label="訂單處理工作區">
          <button type="button" className={workspaceView === "create" ? "is-active" : ""} onClick={() => switchWorkspace("create")}>建立任務</button>
          <button type="button" className={workspaceView === "queue" ? "is-active" : ""} onClick={() => switchWorkspace("queue", "all")}>處理工作區</button>
          <button type="button" className={workspaceView === "history" ? "is-active" : ""} onClick={() => switchWorkspace("history", "handled")}>歷史與匯出</button>
          <button type="button" className={workspaceView === "queue" && serviceFilter === "abnormal" ? "is-active is-warning" : "is-warning"} onClick={() => switchWorkspace("queue", "abnormal")}>需要協助 <strong>{dashboardCounts.needsHelp}</strong></button>
        </nav>

        {workspaceView === "create" && (
        <div className="tl-workspace-stack">
        <div className="tl-panel tl-panel-pad tl-auto-mode-panel">
          <div>
            <div className="tl-panel-title">換貨執行模式</div>
            <div className="tl-panel-desc">
              {autoRunSettings.jamboAutoRun
                ? "只影響換貨任務：客人送出後，系統會自動逐筆換貨；有設定差額時也會自動退還紅利。"
                : "只影響換貨任務：客人送出後先建立待處理資料，客服需在此頁逐筆點擊執行。"}
            </div>
          </div>
          <div className="tl-auto-mode-actions">
            <button
              type="button"
              className={`tl-service-tab ${!autoRunSettings.jamboAutoRun ? "is-active" : ""}`}
              onClick={() => updateAutoRunMode(false)}
              disabled={isLoading}
            >
              人工確認
            </button>
            <button
              type="button"
              className={`tl-service-tab ${autoRunSettings.jamboAutoRun ? "is-active" : ""}`}
              onClick={() => updateAutoRunMode(true)}
              disabled={isLoading}
            >
              全自動換貨
            </button>
          </div>
        </div>

        <RuleCommodityOrderSearch
          products={filteredRuleSearchProducts}
          totalCount={ruleSearchProducts.length}
          keyword={ruleKeyword}
          onKeywordChange={setRuleKeyword}
          selectedCommodity={selectedCommodity}
          selectedCommodities={selectedCommodities}
          multiSelectEnabled={importMode === "all"}
          onPick={handlePickCommodity}
        />

        <div className="tl-panel tl-panel-pad">
          <div className="tl-panel-head">
            <div>
              <div className="tl-panel-title">訂單任務搜尋</div>
              <div className="tl-panel-desc">
                {selectedCommodity
                  ? `${selectedCommodities.length > 1
                    ? `目前已選 ${selectedCommodities.length} 個商品`
                    : `目前商品：${selectedCommodity.partNumber || selectedCommodity.commodityId} ${selectedCommodity.name}`} / ${taskType === "form" ? "建立表單任務" : "建立換貨任務"}`
                  : "請先從已建立規則選擇缺貨商品"}
              </div>
            </div>
          </div>
          {taskType === "exchange" && selectedCommodity && (
            <div className={`tl-create-difference-setting ${refundDifferenceEnabled ? "is-enabled" : ""}`}>
              <label className="tl-check-option">
                <input
                  type="checkbox"
                  checked={refundDifferenceEnabled}
                  onChange={(event) => setRefundDifferenceEnabled(event.target.checked)}
                />
                <span>
                  <strong>退還差額紅利</strong>
                  <small>套用到這次建立的所有換貨任務</small>
                </span>
              </label>
              {refundDifferenceEnabled && (
                <label className="tl-labeled-field tl-create-difference-amount">
                  <span>每筆退還點數</span>
                  <span className="tl-difference-reward-field">
                    <input
                      className="modern-input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="1"
                      inputMode="numeric"
                      value={refundDifferenceAmount}
                      onChange={(event) => setRefundDifferenceAmount(event.target.value)}
                      placeholder="輸入差額"
                    />
                    <em>點</em>
                  </span>
                </label>
              )}
              <small className="tl-create-difference-hint">
                人工模式會在執行換貨後退點；全自動模式會一併自動退點。
              </small>
            </div>
          )}
          {selectedCommodities.length > 1 ? (
            <div className="tl-row-sub" style={{ marginBottom: 12 }}>
              已選 {selectedCommodities.length} 個商品，會依各商品規則設定的樣式查詢全部訂單。
            </div>
          ) : commodityStyles.length > 0 && (
            <div className="tl-style-chip-row" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className={`tl-style-chip ${selectedStyles.length === 0 ? "is-selected" : ""}`}
                onClick={() => setSelectedStyles([])}
              >
                <span>全部樣式</span>
              </button>
              {commodityStyles.map((style) => (
                <button
                  type="button"
                  key={style}
                  className={`tl-style-chip ${selectedStyles.includes(style) ? "is-selected" : ""}`}
                  onClick={() => toggleStyle(style)}
                >
                  <span>{style}</span>
                </button>
              ))}
            </div>
          )}
          <div className="tl-form-row">
            <label className="tl-labeled-field">
              <span>開始時間</span>
              <input
                className="modern-input"
                type="datetime-local"
                value={searchBegin}
                onChange={(e) => setSearchBegin(e.target.value)}
              />
            </label>
            <label className="tl-labeled-field">
              <span>結束時間</span>
              <input
                className="modern-input"
                type="datetime-local"
                value={searchEnd}
                onChange={(e) => setSearchEnd(e.target.value)}
              />
            </label>
            <label className="tl-labeled-field">
              <span>匯入模式</span>
              <select
                className="modern-input"
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
              >
                <option value="all">全部訂單</option>
                <option value="missingQty" disabled={selectedCommodities.length > 1}>指定缺貨數量</option>
              </select>
              {selectedCommodities.length > 1 && (
                <small className="tl-field-hint">多選商品時固定使用全部訂單。</small>
              )}
            </label>
            {importMode === "missingQty" && (
              <label className="tl-labeled-field tl-number-field">
                <span>缺貨件數</span>
                <input
                  className="modern-input"
                  type="number"
                  min="1"
                  value={missingQty}
                  onChange={(e) => setMissingQty(e.target.value)}
                />
              </label>
            )}
            <label className="tl-labeled-field">
              <span>排序方式</span>
              <select
                className="modern-input"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
              >
                <option value="latestPaid">最晚付款</option>
                <option value="latestClosed">最後結單/成立時間</option>
              </select>
            </label>
            <label className="tl-check-option tl-order-search-check">
              <input
                type="checkbox"
                checked={onlyUnpreparedOrders}
                onChange={(e) => setOnlyUnpreparedOrders(e.target.checked)}
              />
              <span>只抓未配貨訂單</span>
            </label>
          </div>
          <div className="tl-order-search-actions">
            <div className="tl-order-search-action-group">
              <button
                className="modern-btn modern-btn-primary"
                onClick={searchOrders}
                disabled={isLoading}
              >
                查直播訂單
              </button>
              <button
                className="modern-btn modern-btn-secondary"
                onClick={importOrders}
                disabled={isLoading || selectedOrders.length === 0}
              >
                匯入已勾選 {selectedOrders.length} 筆
              </button>
            </div>
            <div className="tl-order-search-action-group tl-order-search-excel-group">
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={downloadOutOfStockExcelTemplate}
                disabled={isLoading}
              >
                下載 Excel 範本
              </button>
              <label
                className={`modern-btn modern-btn-secondary tl-file-button ${excelPreviewProgress?.active ? "is-loading" : ""}`}
              >
                {excelPreviewProgress?.active
                  ? `${String(excelPreviewProgress.stage || "").includes("匯入") ? "匯入中" : "查詢中"} ${excelPreviewProgressPercent}%`
                  : "匯入 Excel 預覽"}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleOutOfStockExcelPreview}
                  disabled={isLoading}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={importExcelPreviewOrders}
                disabled={
                  isLoading ||
                  !(excelPreview?.rows || []).some(
                    (row) => row.canImport && selectedExcelRowKeys.has(row.rowKey),
                  )
                }
              >
                匯入 Excel 勾選列
              </button>
            </div>
          </div>
          {excelPreviewProgress && (
            <div className="tl-excel-progress-card">
              <div className="tl-excel-progress-head">
                <strong>{excelPreviewProgress.stage || "正在處理 Excel"}</strong>
                <span>
                  {excelPreviewProgress.completedRows || 0}/
                  {excelPreviewProgress.searchableRows || 0} 列
                  {excelPreviewProgress.totalBatches
                    ? `｜批次 ${excelPreviewProgress.completedBatches || 0}/${excelPreviewProgress.totalBatches}`
                    : ""}
                </span>
              </div>
              <div
                className="tl-excel-progress-track"
                aria-label="Excel 查詢進度"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={excelPreviewProgressPercent}
                role="progressbar"
              >
                <div
                  className="tl-excel-progress-bar"
                  style={{ width: `${excelPreviewProgressPercent}%` }}
                />
              </div>
              <div className="tl-excel-progress-meta">
                <span>可匯入 {excelPreviewProgress.canImportRows || 0} 列</span>
                <span>需確認 {excelPreviewProgress.failedRows || 0} 列</span>
                {excelPreviewProgress.active && <span>請稍候，不要關閉頁面</span>}
              </div>
            </div>
          )}
          {(excelPreview?.rows || []).length > 0 && (
            <div className="tl-jambo-preview" style={{ marginTop: 14 }}>
              <div className="tl-jambo-preview-head">
                <strong>Excel 預覽結果</strong>
                <span>
                  共 {excelPreview.totalRows || excelPreview.rows.length} 列，
                  可匯入 {excelPreview.rows.filter((row) => row.canImport).length} 列，
                  已勾選 {selectedExcelRowKeys.size} 列，
                  預計匯入{" "}
                  {excelPreview.rows
                    .filter(
                      (row) =>
                        row.canImport && selectedExcelRowKeys.has(row.rowKey),
                    )
                    .reduce(
                      (sum, row) => sum + (Number(row.selectedQty || 0) || 0),
                      0,
                    )}{" "}
                  件
                </span>
              </div>
              <div className="tl-jambo-preview-list">
                {excelPreview.rows.map((row) => (
                  <div
                    key={row.rowKey}
                    className={`tl-jambo-preview-row ${row.canImport ? "" : "is-disabled"}`}
                  >
                    <label className="tl-jambo-preview-check">
                      <input
                        type="checkbox"
                        checked={selectedExcelRowKeys.has(row.rowKey)}
                        disabled={!row.canImport || isLoading}
                        onChange={() => toggleExcelPreviewRow(row.rowKey)}
                      />
                    </label>
                    <div className="tl-jambo-preview-main">
                      <div className="tl-jambo-preview-title">
                        {row.itemNumber} {row.style ? `／${row.style}` : "／無樣式"}
                        <span>需求 {row.quantity || 0} 件</span>
                      </div>
                      <div className="tl-jambo-preview-sub">
                        {row.commodity
                          ? `${row.commodity.name || row.commodity.partNumber || row.itemNumber}｜規則 ${row.matchedRulesCount || 0} 筆`
                          : "尚未匹配直播商品"}
                        {row.sourceRows?.length
                          ? `｜來源列 ${row.sourceRows.join("、")}`
                          : ""}
                      </div>
                      {row.errors?.length > 0 && (
                        <div className="tl-jambo-preview-error">
                          {row.errors.join("；")}
                        </div>
                      )}
                      {row.canImport && (
                        <div className="tl-jambo-preview-orders">
                          付款單 {row.paCount || 0} 張｜符合訂單{" "}
                          {(row.orders || []).length} 筆｜預計匯入{" "}
                          {row.selectedQty || 0}/{row.requestedQty || row.quantity} 件
                          {(row.selectedOrders || []).slice(0, 6).map((order) => (
                            <span
                              key={`${row.rowKey}_${order.jamboOrderItemId}_${order.paNumber}`}
                            >
                              {formatPaNumber(order.paNumber)} x {order.takeQty || 1}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {orders.length > 0 && (
            <div className="tl-list-stack" style={{ marginTop: 14 }}>
              <div className="tl-order-result-toolbar">
                <span>
                  查到 {orders.length} 筆 / {searchSummary?.paCount || 0} 張 PA / 商品 {searchSummary?.itemQuantityTotal || 0} 件，已勾選 {selectedOrders.length} 筆，將匯入 {searchSummary?.selectedQty || 0} 件
                  {searchSummary?.onlyUnprepared ? "，已排除配貨完成訂單" : ""}
                  {searchSummary?.zeroQuantitySkippedCount ? `，已略過數量 0：${searchSummary.zeroQuantitySkippedCount} 筆` : ""}
                </span>
                <div>
                  <button
                    type="button"
                    className="modern-btn modern-btn-secondary"
                    onClick={selectVisibleOrders}
                    disabled={isLoading}
                  >
                    全選結果
                  </button>
                  <button
                    type="button"
                    className="modern-btn modern-btn-secondary"
                    onClick={() => setSelectedOrders([])}
                    disabled={isLoading || selectedOrders.length === 0}
                  >
                    清除勾選
                  </button>
                </div>
              </div>
              {orders.slice(0, 20).map((order) => (
                <div
                  key={order.jamboOrderItemId}
                  className={`tl-order-row tl-jambo-search-order-row ${selectedOrderIds.has(String(order.jamboOrderItemId)) ? "is-selected" : ""} ${!isOrderSelectable(order) ? "is-disabled" : ""}`}
                >
                  <div className="tl-admin-customer-line">
                    <label
                      className="tl-service-check"
                      aria-label={`選擇 ${order.paNumber || order.fromName || "直播訂單"}`}
                    >
                      <input
                        type="checkbox"
                        disabled={!isOrderSelectable(order)}
                        checked={selectedOrderIds.has(
                          String(order.jamboOrderItemId),
                        )}
                        onChange={() => toggleOrderSelection(order)}
                      />
                    </label>
                    {order.fromPicture ? (
                      <img
                        src={order.fromPicture}
                        alt={order.fromName || order.paNumber || "客戶"}
                        className="tl-admin-avatar"
                      />
                    ) : (
                      <div className="tl-admin-avatar tl-admin-avatar-placeholder">
                        客
                      </div>
                    )}
                    <div>
                      <div className="tl-row-title">
                        {order.paNumber} / {order.fromName || "未命名客戶"}
                      </div>
                  <div className="tl-row-sub">
                        電話：{order.recipientPhone || "無電話"} / 金額：$
                        {order.amount || 0} / 樣式：{order.style || "-"} /
                        數量：{order.quantity || 1}
                        {order.existingExchangeCount ? ` / 已匯入 ${order.existingExchangeCount}` : ""}
                        {isOrderSelectable(order) ? ` / 可匯入 ${availableImportQtyOf(order)}` : ` / ${order.duplicateReason || "不可匯入"}`}
                      </div>
                      <div className="tl-row-sub">
                        成立：{order.billCreatedAtText || formatDeadline(order.billCreatedAt) || "-"} / 付款：
                        {order.billPaidAtText || formatDeadline(order.billPaidAt || order.paidAt) || "-"} / 排序：
                        {order.sortMode === "latestClosed" ? "最後結單/成立時間" : "最晚付款"}
                      </div>
                    </div>
                  </div>
                  <div className="tl-row-actions">
                    {selectedOrderIds.has(String(order.jamboOrderItemId)) && (
                      <span className="tl-pill">
                        將匯入{" "}
                        <strong>
                          {selectedTakeQtyOf(order, selectedOrders)}
                        </strong>
                      </span>
                    )}
                    <span className="tl-pill">
                      排序{" "}
                      <strong>{orderSortDisplayTextOf(order) || "-"}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {orders.length === 0 && searchSummary && (
            <div className="tl-empty-state" style={{ marginTop: 14 }}>
              系統查到 {searchSummary.rawCount} 筆訂單，符合這個商品與樣式的有{" "}
              {searchSummary.filteredCount} 筆
              {searchSummary.onlyUnprepared ? "未配貨資料" : "資料"}
              {searchSummary.zeroQuantitySkippedCount
                ? `，另略過 ${searchSummary.zeroQuantitySkippedCount} 筆數量 0 訂單`
                : ""}
              。若查不到，請放寬時間或確認目前商店是否正確。
            </div>
          )}
        </div>
        </div>
        )}

        {workspaceView !== "create" && (
        <div className="tl-panel tl-panel-pad tl-order-queue-panel">
          <div className="tl-panel-head">
            <div>
              <div className="tl-panel-title">處理佇列</div>
              <div className="tl-panel-desc">
                客人送出的換貨選擇與表單答案會直接出現在這裡；完成後標記已處理即可。
              </div>
            </div>
            <div className="tl-order-queue-counts">
              <span><strong>{filteredExchangeItems.length + filteredServiceTasks.length}</strong> 筆符合</span>
              <span>換貨 {filteredExchangeItems.length}</span>
              <span>表單 {filteredServiceTasks.length}</span>
              {filteredExchangeItems.length + filteredServiceTasks.length === 0 && <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setSmsDialog((prev) => ({ ...prev, open: true, mode: "history", smsKind: "form", messages: [], quickConfirm: null }))}>簡訊通知</button>}
            </div>
          </div>
          <div className="tl-service-toolbar">
            <div className="tl-service-tabs">
              {queueTabs.map((tab) => (
                <button
                  type="button"
                  key={tab.id}
                  className={`tl-service-tab ${serviceFilter === tab.id ? "is-active" : ""}`}
                  onClick={() => setServiceFilter(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
              <label className="tl-queue-more-filter">
                <span>更多狀態</span>
                <select
                  className="modern-select"
                  value={queueMoreFilters.some((filter) => filter.id === serviceFilter) ? serviceFilter : ""}
                  onChange={(event) => event.target.value && setServiceFilter(event.target.value)}
                >
                  <option value="">選擇狀態</option>
                  {queueMoreFilters.map((filter) => (
                    <option key={filter.id} value={filter.id}>{filter.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <input
              className="modern-input tl-service-search"
              value={queueKeyword}
              placeholder="搜尋 PA、電話、客戶、商品、料號"
              onChange={(event) => setQueueKeyword(event.target.value)}
            />
            <label className="tl-processing-sort-control">
              <span>排列</span>
              <select
                className="modern-select"
                value={processingSortMode}
                onChange={(event) => setProcessingSortMode(event.target.value)}
              >
                {processingSortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <details className="tl-order-insights">
            <summary>
              <span>
                <strong>商品與表單統計</strong>
                <small>需要盤點數量或匯出前再展開</small>
              </span>
              <em>展開</em>
            </summary>
            <div className="tl-order-insights-body">
          <div className="tl-exchange-result-summary tl-pending-source-summary">
            <div className="tl-result-summary-head">
              <div>
                <div className="tl-panel-title">待換貨商品統計</div>
                <div className="tl-panel-desc">
                  依目前篩選條件統計缺貨商品數量；同商品多樣式會合併在同一張卡。
                </div>
              </div>
              <div className="tl-result-total-grid">
                <span>
                  待換件數 <strong>{pendingSourceSummary.totalQuantity}</strong>
                </span>
                <span>
                  缺貨商品款數 <strong>{pendingSourceSummary.productCount}</strong>
                </span>
                <span>
                  PA 單數 <strong>{pendingSourceSummary.paCount}</strong>
                </span>
              </div>
            </div>
            {pendingSourceSummary.rows.length > 0 ? (
              <div className="tl-result-product-grid">
                {pendingSourceSummary.rows.map((row) => (
                  <div className="tl-result-product-card tl-source-summary-card" key={row.key}>
                    {row.image ? (
                      <img
                        src={row.image}
                        alt={row.productName}
                        className="tl-result-product-image"
                      />
                    ) : (
                      <div className="tl-result-product-image tl-result-product-placeholder">
                        無圖
                      </div>
                    )}
                    <div className="tl-result-product-copy">
                      <strong>{row.productName}</strong>
                      <span>料號：{row.itemNumber || "-"}</span>
                      <small>{row.paCount} 張付款單</small>
                      <div className="tl-style-count-list">
                        {row.styleRows.map((styleRow) => (
                          <em key={styleRow.style}>
                            {styleRow.style || "無樣式"} x {styleRow.quantity}
                          </em>
                        ))}
                      </div>
                    </div>
                    <div className="tl-result-product-count">
                      <strong>{row.quantity}</strong>
                      <span>件</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tl-empty-state tl-result-empty">
                目前篩選範圍內沒有待換貨商品。
              </div>
            )}
          </div>
          <div className="tl-exchange-result-summary">
            <div className="tl-result-summary-head">
              <div>
                <div className="tl-panel-title">換貨結果統計</div>
                <div className="tl-panel-desc">
                  依目前篩選條件統計客人選擇的換貨後商品與數量；同商品多樣式會合併在同一張卡。
                </div>
              </div>
              <div className="tl-result-total-grid">
                <span>
                  換貨數量 <strong>{exchangeResultSummary.exchangeCount}</strong>
                </span>
                <span>
                  商品款數 <strong>{exchangeResultSummary.rows.length}</strong>
                </span>
                <span>
                  不換貨 <strong>{exchangeResultSummary.cancelCount}</strong>
                </span>
              </div>
            </div>
            {exchangeResultSummary.rows.length > 0 ? (
              <div className="tl-result-product-grid">
                {exchangeResultSummary.rows.map((row) => (
                  <div className="tl-result-product-card" key={row.key}>
                    {row.image ? (
                      <img
                        src={row.image}
                        alt={row.productName}
                        className="tl-result-product-image"
                      />
                    ) : (
                      <div className="tl-result-product-image tl-result-product-placeholder">
                        無圖
                      </div>
                    )}
                    <div className="tl-result-product-copy">
                      <strong>{row.productName}</strong>
                      <span>料號：{row.itemNumber || "-"}</span>
                      <small>{row.paCount} 張付款單</small>
                      <div className="tl-style-count-list">
                        {row.styleRows.map((styleRow) => (
                          <em key={styleRow.style}>
                            {styleRow.style || "無樣式"} x {styleRow.quantity}
                          </em>
                        ))}
                      </div>
                    </div>
                    <div className="tl-result-product-count">
                      <strong>{row.quantity}</strong>
                      <span>件</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tl-empty-state tl-result-empty">
                目前篩選範圍內尚無客人選擇的換貨商品。
              </div>
            )}
          </div>
          <div className="tl-exchange-result-summary tl-service-form-summary">
            <div className="tl-result-summary-head">
              <div>
                <div className="tl-panel-title">表單任務統計</div>
                <div className="tl-panel-desc">
                  依目前篩選條件統計客人填寫與完成狀態；同表單會合併顯示。
                </div>
              </div>
              <div className="tl-result-total-grid tl-service-form-total-grid">
                <span>
                  表單任務 <strong>{serviceFormSummary.totalTasks}</strong>
                </span>
                <span>
                  待填寫 <strong>{serviceFormSummary.pendingCount}</strong>
                </span>
                <span>
                  客人已送出 <strong>{serviceFormSummary.submittedCount}</strong>
                </span>
                <span>
                  需處理 <strong>{serviceFormSummary.needsHelpCount}</strong>
                </span>
              </div>
            </div>
            {serviceFormSummary.rows.length > 0 ? (
              <div className="tl-form-summary-grid">
                {serviceFormSummary.rows.map((row) => (
                  <div className="tl-form-summary-card" key={row.key}>
                    <div className="tl-form-summary-copy">
                      <span>表單</span>
                      <strong>{row.formTitle}</strong>
                      <small>{row.productText}</small>
                    </div>
                    <div className="tl-form-summary-metrics">
                      <span>
                        任務 <strong>{row.taskCount}</strong>
                      </span>
                      <span>
                        PA <strong>{row.paCount}</strong>
                      </span>
                      <span>
                        已填項目 <strong>{row.answeredFields}</strong>
                      </span>
                    </div>
                    <div className="tl-form-summary-status-row">
                      <em>待填 {row.pendingCount}</em>
                      <em>已送出 {row.submittedCount}</em>
                      {row.handledCount > 0 && <em>已處理 {row.handledCount}</em>}
                      {row.needsHelpCount > 0 && <em className="is-warning">需處理 {row.needsHelpCount}</em>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tl-empty-state tl-result-empty">
                目前篩選範圍內沒有表單任務。
              </div>
            )}
          </div>
            </div>
          </details>
          {filteredExchangeItems.length > 0 && renderExchangeBatchBar("top")}
          {filteredServiceTasks.length > 0 && renderServiceTaskBatchBar()}
          <div className="tl-list-stack">
            {displayGroups.map((group) => {
              if (group.kind === "exchange") {
              const groupIds = group.items.map((item) => item.id);
              const groupSelected = groupIds.every((id) =>
                selectedExchangeIds.includes(id),
              );
              const customerPhoto =
                group.fromPicture ||
                (group.fromId
                  ? `https://graph.facebook.com/${group.fromId}/picture`
                  : "");
              return (
                <div
                  key={group.key}
                  className={`tl-exchange-group tl-service-card-${group.statusTone}`}
                >
                  <div className="tl-exchange-group-head">
                    <label className="tl-service-check">
                      <input
                        type="checkbox"
                        checked={groupSelected}
                        onChange={() => toggleExchangeGroupSelection(groupIds)}
                      />
                    </label>
                    {customerPhoto ? (
                      <img
                        src={customerPhoto}
                        alt={group.fromName || group.paNumber}
                        className="tl-admin-avatar"
                      />
                    ) : (
                      <div className="tl-admin-avatar tl-admin-avatar-placeholder">
                        客
                      </div>
                    )}
                    <div className="tl-service-customer-copy">
                      <div className="tl-service-pa-row">
                        <span className="tl-row-title">
                          付款單：{group.paNumber}
                        </span>
                        <span
                          className={`tl-service-status tl-service-status-${group.statusTone}`}
                        >
                          {group.status}
                        </span>
                        <span className="tl-pill">
                          {group.items.length} 筆換貨
                        </span>
                      </div>
                      <div className="tl-row-sub">
                        客戶：{group.fromName || "-"} / 電話：
                        {group.recipientPhone || "-"}
                      </div>
                    </div>
                    <div className="tl-row-actions">
                      <button
                        className="modern-btn modern-btn-secondary"
                        onClick={() => openCustomerLinkDialog(group.paNumber)}
                        disabled={isLoading}
                      >
                        客人安全連結
                      </button>
                      <button
                        className="modern-btn modern-btn-secondary"
                        onClick={() => toggleExchangeGroupSelection(groupIds)}
                        disabled={isLoading}
                      >
                        {groupSelected ? "取消整組勾選" : "勾選整組"}
                      </button>
                    </div>
                  </div>
                  <div className="tl-exchange-group-items">
                    {group.items.map((item) => renderExchangeItemCard(item))}
                  </div>
                </div>
              );

              }

              const groupIds = group.items.map((task) => task.id);
              const groupSelected = groupIds.every((id) =>
                selectedServiceTaskIds.includes(id),
              );
              const customerPhoto =
                group.fromPicture ||
                (group.fromId
                  ? `https://graph.facebook.com/${group.fromId}/picture`
                  : "");
              return (
                <div
                  key={group.key}
                  className={`tl-exchange-group tl-service-card-${group.statusTone}`}
                >
                  <div className="tl-exchange-group-head">
                    <label className="tl-service-check">
                      <input
                        type="checkbox"
                        checked={groupSelected}
                        onChange={() => toggleServiceGroupSelection(groupIds)}
                      />
                    </label>
                    {customerPhoto ? (
                      <img
                        src={customerPhoto}
                        alt={group.fromName || group.paNumber}
                        className="tl-admin-avatar"
                      />
                    ) : (
                      <div className="tl-admin-avatar tl-admin-avatar-placeholder">
                        客
                      </div>
                    )}
                    <div className="tl-service-customer-copy">
                      <div className="tl-service-pa-row">
                        <span className="tl-row-title">
                          付款單：{group.paNumber}
                        </span>
                        <span
                          className={`tl-service-status tl-service-status-${group.statusTone}`}
                        >
                          {group.status}
                        </span>
                        <span className="tl-pill">
                          {group.items.length} 筆表單
                        </span>
                      </div>
                      <div className="tl-row-sub">
                        客戶：{group.fromName || "-"} / 電話：
                        {group.recipientPhone || "-"}
                      </div>
                    </div>
                    <div className="tl-row-actions">
                      <button
                        className="modern-btn modern-btn-secondary"
                        onClick={() => openCustomerLinkDialog(group.paNumber)}
                        disabled={isLoading}
                      >
                        客人安全連結
                      </button>
                      <button
                        className="modern-btn modern-btn-secondary"
                        onClick={() => toggleServiceGroupSelection(groupIds)}
                        disabled={isLoading}
                      >
                        {groupSelected ? "取消整組勾選" : "勾選整組"}
                      </button>
                    </div>
                  </div>
                  <div className="tl-exchange-group-items">
                    {group.items.map((task) => renderServiceTaskCard(task))}
                  </div>
                </div>
              );
            })}
            {filteredExchangeItems.length === 0 && filteredServiceTasks.length === 0 && (
              <div className="tl-empty-state">
                目前沒有符合篩選的售後任務。可切到「全部」或放寬搜尋字。
              </div>
            )}
          </div>
          {filteredExchangeItems.length > 0 && renderExchangeBatchBar("bottom")}
        </div>
        )}
      </div>
      {differenceTaskDialog.open && (
        <div
          className="tl-admin-modal-overlay"
          role="presentation"
          onClick={() => setDifferenceTaskDialog((current) => ({ ...current, open: false }))}
        >
          <div
            className="tl-admin-modal tl-difference-task-modal"
            role="dialog"
            aria-modal="true"
            aria-label="編輯換貨任務"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">編輯換貨任務</div>
                <div className="tl-panel-desc">付款單 {differenceTaskDialog.paNumber || "-"}</div>
              </div>
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() => setDifferenceTaskDialog((current) => ({ ...current, open: false }))}
              >
                關閉
              </button>
            </div>
            <div className={`tl-create-difference-setting ${differenceTaskDialog.enabled ? "is-enabled" : ""}`}>
              <label className="tl-check-option">
                <input
                  type="checkbox"
                  checked={differenceTaskDialog.enabled}
                  onChange={(event) => setDifferenceTaskDialog((current) => ({
                    ...current,
                    enabled: event.target.checked,
                  }))}
                />
                <span>
                  <strong>退還差額紅利</strong>
                  <small>只套用到這一筆換貨任務</small>
                </span>
              </label>
              {differenceTaskDialog.enabled && (
                <label className="tl-labeled-field tl-create-difference-amount">
                  <span>退還點數</span>
                  <span className="tl-difference-reward-field">
                    <input
                      className="modern-input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="1"
                      inputMode="numeric"
                      value={differenceTaskDialog.amount}
                      onChange={(event) => setDifferenceTaskDialog((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))}
                      placeholder="輸入差額"
                    />
                    <em>點</em>
                  </span>
                </label>
              )}
            </div>
            <div className="tl-admin-modal-actions">
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() => setDifferenceTaskDialog((current) => ({ ...current, open: false }))}
                disabled={isLoading}
              >
                取消
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={saveDifferenceTaskEditor}
                disabled={isLoading}
              >
                {isLoading ? "儲存中..." : "儲存設定"}
              </button>
            </div>
          </div>
        </div>
      )}
      {customerLinkDialog.open && (
        <div className="tl-admin-modal-overlay" role="presentation" onClick={() => setCustomerLinkDialog((previous) => ({ ...previous, open: false }))}>
          <div className="tl-admin-modal tl-customer-link-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">客人安全連結</div>
                <div className="tl-panel-desc">付款單 {customerLinkDialog.paNumber} 與同商店、同收件電話的待處理訂單；連結不包含手機或 PA 單號。</div>
              </div>
              <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setCustomerLinkDialog((previous) => ({ ...previous, open: false }))}>關閉</button>
            </div>
            {!customerLinkDialog.url ? (
              <div className="tl-customer-link-setup">
                <label className="tl-labeled-field">
                  <span>有效時間</span>
                  <select className="modern-input" value={customerLinkDialog.expiresInHours} onChange={(event) => setCustomerLinkDialog((previous) => ({ ...previous, expiresInHours: Number(event.target.value) }))}>
                    <option value={24}>1 天</option>
                    <option value={72}>3 天</option>
                    <option value={168}>7 天</option>
                    <option value={336}>14 天</option>
                    <option value={720}>30 天</option>
                  </select>
                </label>
                <button type="button" className="modern-btn modern-btn-primary" onClick={createCustomerAccessLink} disabled={customerLinkDialog.loading}>
                  {customerLinkDialog.loading ? "建立中..." : "建立安全連結與 QR Code"}
                </button>
                {customerLinkDialog.error && <div className="tl-error-text">{customerLinkDialog.error}</div>}
              </div>
            ) : (
              <div className="tl-customer-link-result">
                {customerLinkDialog.qrDataUrl && <img src={customerLinkDialog.qrDataUrl} alt={`${customerLinkDialog.paNumber} 客人安全連結 QR Code`} />}
                <div>
                  <strong>連結已建立</strong>
                  <p>到期：{formatDeadline(customerLinkDialog.expiresAt)}</p>
                  <input className="modern-input" value={customerLinkDialog.url} readOnly onFocus={(event) => event.target.select()} />
                  <button type="button" className="modern-btn modern-btn-primary" onClick={copyCustomerAccessLink}>{customerLinkDialog.copied ? "已複製" : "複製連結"}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {deadlineDialog.open && (
        <div className="tl-admin-modal-overlay" role="presentation" onClick={() => setDeadlineDialog((prev) => ({ ...prev, open: false }))}>
          <div className="tl-admin-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">{deadlineDialog.target === "service" ? "設定表單期限" : "設定換貨期限"}</div>
                <div className="tl-panel-desc">
                  已選 {deadlineDialog.target === "service" ? selectedServiceTaskIds.length : selectedExchangeIds.length} 筆資料，會套用同一個截止時間。
                </div>
              </div>
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() => setDeadlineDialog((prev) => ({ ...prev, open: false }))}
              >
                關閉
              </button>
            </div>
            <div className="tl-panel-desc" role="status">
              <strong>{deadlineDialog.mixed ? "所選資料目前期限不同" : "目前期限"}</strong>
              {deadlineDialog.groups.map((group) => <div key={group.value || "none"}>{group.value ? formatDeadline(group.value) : "未設定期限"} · {group.count} 筆</div>)}
              {deadlineDialog.mixed && <p>選擇新時間會統一更新所選資料；解除期限則全部改為不設期限。</p>}
            </div>
            <div className="tl-sms-options">
              <label className="tl-labeled-field">
                <span>截止時間（台灣時間）</span>
                <input
                  className="modern-input"
                  type="datetime-local"
                  value={deadlineDialog.deadline}
                  onChange={(event) => setDeadlineDialog((prev) => ({ ...prev, deadline: event.target.value }))}
                />
              </label>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={() => deadlineDialog.target === "service" ? setSelectedServiceTaskDeadline(false) : setSelectedDeadlineBatch(false)}
                disabled={
                  isLoading ||
                  (deadlineDialog.target === "service"
                    ? selectedServiceTaskIds.length === 0
                    : selectedExchangeIds.length === 0) ||
                  !deadlineDialog.deadline
                }
              >
                {isLoading ? "設定中..." : "套用截止時間"}
              </button>
              <button type="button" className="modern-btn modern-btn-secondary" disabled={isLoading || !deadlineDialog.hasDeadline}
                onClick={() => deadlineDialog.target === "service" ? setSelectedServiceTaskDeadline(true) : setSelectedDeadlineBatch(true)}>
                解除期限
              </button>
            </div>
          </div>
        </div>
      )}
      {removeDialog.open && (
        <div
          className="tl-admin-modal-overlay"
          role="presentation"
          onClick={() =>
            !isLoading &&
            setRemoveDialog({ open: false, ids: [], target: "exchange", title: "", message: "" })
          }
        >
          <div
            className="tl-admin-modal tl-admin-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">{removeDialog.title}</div>
                <div className="tl-panel-desc">{removeDialog.message}</div>
              </div>
            </div>
            <div className="tl-sms-confirm-actions">
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() =>
                  setRemoveDialog({
                    open: false,
                    ids: [],
                    target: "exchange",
                    title: "",
                    message: "",
                  })
                }
                disabled={isLoading}
              >
                取消
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-danger"
                onClick={confirmRemoveItems}
                disabled={isLoading}
              >
                {isLoading ? "移除中..." : "確認移除"}
              </button>
            </div>
          </div>
        </div>
      )}
      {returnDialog.open && (
        <div
          className="tl-admin-modal-overlay"
          role="presentation"
          onClick={() =>
            !isLoading &&
            setReturnDialog({
              open: false,
              ids: [],
              title: "",
              message: "",
              mode: "return",
            })
          }
        >
          <div
            className="tl-admin-modal tl-admin-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">{returnDialog.title}</div>
                <div className="tl-panel-desc">{returnDialog.message}</div>
              </div>
            </div>
            <div className="tl-detail-box">
              {returnDialog.mode === "cancelHandled"
                ? "這只會取消工作人員的已處理標記，讓資料回到原本狀態；不會清除客人選擇，也不會操作直播訂單。"
                : "若資料已完成直播換貨，系統會先把該筆直播訂單換回原本缺貨商品，再清除客人選擇。若客人選的是「不換貨，申請退款」，則不會操作直播訂單。"}
            </div>
            <div className="tl-sms-confirm-actions">
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() =>
                  setReturnDialog({
                    open: false,
                    ids: [],
                    title: "",
                    message: "",
                    mode: "return",
                  })
                }
                disabled={isLoading}
              >
                取消
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-danger"
                onClick={confirmReturnExchangeItems}
                disabled={isLoading}
              >
                {returnDialog.mode === "cancelHandled"
                  ? isLoading
                    ? "取消中..."
                    : "確認取消標記"
                  : isLoading
                    ? "退回中..."
                    : "確認退回並還原"}
              </button>
            </div>
          </div>
        </div>
      )}
      {stockoutRefundDialog.open && (
        <div
          className="tl-admin-modal-overlay"
          role="presentation"
          onClick={() =>
            !isLoading &&
            setStockoutRefundDialog({ open: false, ids: [] })
          }
        >
          <div
            className="tl-admin-modal tl-admin-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">確認執行斷貨退紅利</div>
                <div className="tl-panel-desc">
                  將對 {stockoutRefundDialog.ids.length} 筆直播訂單明細執行斷貨，成功後會自動移到已處理並記錄斷貨時間。
                </div>
              </div>
            </div>
            <div className="tl-detail-box">
              系統會呼叫直播後台斷貨退款 API，將該缺貨商品訂單數量歸 0；若訂單已付款，直播後台會依原本規則先以紅利點數方式退款。
            </div>
            <div className="tl-sms-confirm-actions">
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() => setStockoutRefundDialog({ open: false, ids: [] })}
                disabled={isLoading}
              >
                取消
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-danger"
                onClick={confirmExecuteStockoutRefund}
                disabled={isLoading}
              >
                {isLoading ? "執行中..." : "確認執行斷貨退紅利"}
              </button>
            </div>
          </div>
        </div>
      )}
      {smsDialog.open && (
        <div className="tl-admin-modal-overlay" role="presentation" onClick={() => setSmsDialog((prev) => ({ ...prev, open: false }))}>
          <div className="tl-admin-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="tl-admin-modal-head">
              <div>
                <div className="tl-panel-title">
                  發送{smsKindLabel}簡訊
                </div>
                <div className="tl-panel-desc">
                  {smsDialog.mode && smsDialog.mode !== "immediate" ? "到預約時間才抓取本商店待客人處理的訂單，無需事先勾選。" : <>已選 {activeSmsSelectedCount} 筆資料，同商店、同收件電話的多張 PA 會合併為一封；
                  {smsDialog.smsKind === "stockout"
                    ? "將使用天藍小舖直播斷貨通知文案。"
                    : smsDialog.smsKind === "form"
                      ? "將使用表單簡訊文案，可先預覽，也可直接建立背景發送任務。"
                      : "可先預覽，也可直接建立背景發送任務。"}</>}
                </div>
              </div>
              <button
                type="button"
                className="modern-btn modern-btn-secondary"
                onClick={() => setSmsDialog((prev) => ({ ...prev, open: false }))}
              >
                關閉
              </button>
            </div>
            {smsDialog.smsKind !== "stockout" && <div className="tl-sms-mode-tabs" role="tablist" aria-label="簡訊發送方式">
              {[["immediate", "立即發送"], ["schedule", "預約發送"], ["history", "預約紀錄"]].map(([mode, label]) => <button key={mode} type="button" role="tab" aria-selected={(smsDialog.mode || "immediate") === mode} disabled={smsDialog.preparing || smsDialog.sending} onClick={() => setSmsDialog((prev) => ({ ...prev, mode }))}>{label}</button>)}
            </div>}
            {smsDialog.smsKind !== "stockout" && <div hidden={!smsDialog.mode || smsDialog.mode === "immediate"}>
              <TianlanSmsScheduleManager key={`${currentShop.shopUserId}-${smsDialog.smsKind}`} embedded view={smsDialog.mode || "immediate"} initialKind={smsDialog.smsKind} onViewChange={(mode) => setSmsDialog((prev) => ({ ...prev, mode }))} shopUserId={currentShop.shopUserId} storeName={currentShop.storeName} />
            </div>}
            {(!smsDialog.mode || smsDialog.mode === "immediate" || smsDialog.smsKind === "stockout") && <>
            {activeSmsSelectedCount === 0 && <p className="tl-panel-desc">立即發送需先勾選訂單；預約發送會在時間到時自動抓取名單。</p>}
            <div className="tl-sms-options">
              <label className="modern-form-group">
                <span className="modern-form-label">本次簡訊安全連結效期</span>
                <select className="modern-input" value={smsAccessLinkDays}
                  disabled={smsDialog.preparing || smsDialog.sending}
                  onChange={(event) => {
                    setSmsAccessLinkDays(Number(event.target.value));
                    setSmsDialog((prev) => ({ ...prev, messages: [], quickConfirm: null, copied: false }));
                  }}>
                  {[1, 3, 7, 14, 30].map((days) => <option key={days} value={days}>{days} 天</option>)}
                </select>
                <small>從產生連結起算；更改後須重新產生預覽。表單截止時間另計。</small>
                <a href="/exchange/tianlan/settings#sms-access-link" target="_blank" rel="noreferrer">修改預設效期</a>
              </label>
              <label className="tl-check-option">
                <input
                  type="checkbox"
                  checked={smsDialog.excludeSent}
                  onChange={(event) =>
                    setSmsDialog((prev) => ({
                      ...prev,
                      excludeSent: event.target.checked,
                      quickConfirm: null,
                    }))
                  }
                />
                <span>排除已發送或排隊中的資料</span>
              </label>
              <label className="tl-check-option">
                <input
                  type="checkbox"
                  checked={smsDialog.sendFbMessenger}
                  onChange={(event) =>
                    setSmsDialog((prev) => ({
                      ...prev,
                      sendFbMessenger: event.target.checked,
                      quickConfirm: null,
                    }))
                  }
                />
                <span>
                  同步 FB Messenger 通知
                  <small>所選買家必須在 24 小時內曾主動私訊賣家才能發送</small>
                </span>
              </label>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={prepareSmsBatch}
                disabled={smsDialog.preparing || activeSmsSelectedCount === 0}
              >
                {smsDialog.preparing
                  ? "產生中..."
                  : smsDialog.smsKind === "stockout"
                    ? "產生斷貨簡訊預覽"
                    : smsDialog.smsKind === "form"
                      ? "產生表單簡訊預覽"
                      : "產生簡訊預覽"}
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={quickQueueSmsBatch}
                disabled={smsDialog.sending || smsDialog.preparing || activeSmsSelectedCount === 0}
              >
                直接建立發送任務
              </button>
              <button
                type="button"
                className="modern-btn modern-btn-primary"
                onClick={sendSmsBatch}
                disabled={smsDialog.sending || smsDialog.messages.length === 0}
              >
                {smsDialog.sending ? "建立中..." : "建立發送任務"}
              </button>
            </div>
            {smsDialog.quickConfirm && (
              <div className="tl-sms-confirm-box">
                <strong>確認建立簡訊發送任務</strong>
                <p>
                  將為 {smsDialog.quickConfirm.uniquePaCount} 張 PA 建立 {smsDialog.quickConfirm.recipientCount} 封合併
                  {smsDialog.smsKind === "stockout" ? "斷貨" : smsDialog.smsKind === "form" ? "表單" : ""}簡訊任務
                  {smsDialog.quickConfirm.excludedSentCount
                    ? `，已排除 ${smsDialog.quickConfirm.excludedSentCount} 筆已發送資料`
                    : ""}
                  。建立後會由背景自動發送。
                </p>
                <div className="tl-sms-confirm-actions">
                  <button
                    type="button"
                    className="modern-btn modern-btn-secondary"
                    onClick={() => setSmsDialog((prev) => ({ ...prev, quickConfirm: null }))}
                    disabled={smsDialog.sending}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="modern-btn modern-btn-primary"
                    onClick={sendSmsBatch}
                    disabled={smsDialog.sending}
                  >
                    {smsDialog.sending ? "建立中..." : "確認建立"}
                  </button>
                </div>
              </div>
            )}
            {smsDialog.messages.length > 0 && (
              <>
                <div className="tl-sms-result-head">
                  <strong>已產生 {smsDialog.messages.length} 封合併{smsKindLabel}簡訊</strong>
                  <button
                    type="button"
                    className="modern-btn modern-btn-secondary"
                    onClick={copySmsMessages}
                  >
                    {smsDialog.copied ? "已複製" : "複製全部"}
                  </button>
                </div>
                <div className="tl-sms-message-list">
                  {smsDialog.messages.map((message) => (
                    <div className="tl-sms-message-card" key={message.id || message.paNumber}>
                      <div className="tl-row-title">
                        付款單：{(message.paNumbers || [message.paNumber]).map(formatPaNumber).join("、")} / {message.phone || "無電話"}
                      </div>
                      <p>{message.message}</p>
                      {message.accessLinkExpiresAt && (
                        <small>安全連結到期：{formatDeadline(message.accessLinkExpiresAt)}</small>
                      )}
                      {message.exchangeDeadline && (
                        <small>截止：{formatDeadline(message.exchangeDeadline)}</small>
                      )}
                      {message.status === "sent" && (
                        <small className="tl-success-text">已發送</small>
                      )}
                      {message.status === "queued" && (
                        <small className="tl-success-text">已建立發送任務</small>
                      )}
                      {message.status === "failed" && (
                        <small className="tl-error-text">發送失敗：{message.error || "請稍後重試"}</small>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

function RuleCommodityOrderSearch({
  products,
  totalCount,
  keyword,
  onKeywordChange,
  selectedCommodity,
  selectedCommodities = [],
  multiSelectEnabled = false,
  onPick,
}) {
  const [expandedStyleCards, setExpandedStyleCards] = useState(new Set());
  const pickedId = String(
    selectedCommodity?.commodityId || selectedCommodity?.id || "",
  );
  const pickedIds = new Set(
    (selectedCommodities || []).map((item) => String(item.commodityId || item.id || "")),
  );
  const toggleStyleCard = (id) => {
    setExpandedStyleCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="tl-panel tl-panel-pad tl-rule-order-search">
      <div className="tl-panel-head">
        <div>
          <div className="tl-panel-title">從商品規則選任務商品</div>
          <div className="tl-panel-desc">
            只顯示「商品規則管理」裡已啟用的商品；選定後再查直播訂單，
            建立換貨任務時使用可換商品設定，建立表單任務時使用已綁定的表單題目。
            {multiSelectEnabled ? " 目前可多選商品並以全部訂單查詢。" : " 指定數量模式一次選一個商品。"}
          </div>
        </div>
        <span className="tl-pill">
          可搜尋 <strong>{totalCount}</strong>
        </span>
      </div>

      <div className="tl-form-row tl-rule-search-toolbar">
        <input
          className="modern-input tl-search-input"
          value={keyword}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder="搜尋規則商品料號、品名、商品 ID 或樣式"
        />
      </div>

      {products.length > 0 ? (
        <div className="tl-commodity-grid">
          {products.map((commodity) => {
            const id = String(commodity.commodityId || commodity.id);
            const picked = multiSelectEnabled ? pickedIds.has(id) : pickedId === id;
            const displayStyles =
              commodity.selectedStyles?.length > 0
                ? commodity.selectedStyles
                : commodity.styles || [];
            const expanded = expandedStyleCards.has(id);
            const visibleStyles = expanded
              ? displayStyles
              : displayStyles.slice(0, 4);
            const hiddenStyleCount = Math.max(displayStyles.length - 4, 0);
            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => onPick?.(commodity)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onPick?.(commodity);
                  }
                }}
                className={`tl-commodity-card tl-rule-commodity-card ${picked ? "is-picked" : ""}`}
              >
                <div className="tl-commodity-media-row">
                  {commodity.image ? (
                    <img
                      src={commodity.image}
                      alt={commodity.name}
                      className="tl-commodity-image"
                    />
                  ) : (
                    <div className="tl-commodity-placeholder">規則</div>
                  )}
                  <div>
                    <div className="tl-commodity-name">
                      {commodity.name || "已建立規則商品"}
                    </div>
                    <div className="tl-commodity-meta">
                      料號：{commodity.partNumber || "-"}
                    </div>
                    <div className="tl-commodity-meta">ID：{id}</div>
                  </div>
                </div>
                <div className="tl-rule-card-meta">
                  <span>規則 {commodity.ruleIds?.length || 0}</span>
                  <span>可換商品 {commodity.targetCount || 0}</span>
                  {(commodity.formTaskTemplateId || commodity.questionTemplateId) && (
                    <span>表單已設定</span>
                  )}
                  {commodity.combined && (
                    <span>
                      {commodity.formProductMode === "children"
                        ? `拆分子商品 ${commodity.selectedFormChildKeys?.length || 0} 項`
                        : "整組商品填寫"}
                    </span>
                  )}
                  {commodity.allowCancelRefund && <span>可申請退款</span>}
                </div>
                <div className="tl-rule-style-box">
                  <div className="tl-rule-style-label">樣式</div>
                  {displayStyles.length > 0 ? (
                    <>
                      <div className="tl-rule-style-list">
                        {visibleStyles.map((style) => (
                          <span key={style}>{style}</span>
                        ))}
                      </div>
                      {hiddenStyleCount > 0 && (
                        <button
                          type="button"
                          className="tl-rule-style-toggle"
                          aria-expanded={expanded}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleStyleCard(id);
                          }}
                        >
                          {expanded
                            ? "收合樣式"
                            : `展開全部樣式（另 ${hiddenStyleCount} 個）`}
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="tl-rule-style-empty">不限樣式</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tl-empty-state">
          {totalCount === 0
            ? "尚未建立可用的商品規則。請先到「商品規則管理」建立並啟用商品規則。"
            : "沒有符合關鍵字的規則商品，請換一個料號、品名或樣式搜尋。"}
        </div>
      )}
    </div>
  );
}

export default TianlanJamboOrderManager;
